import { PRINCIPAL_ROUTE_NAME } from "@router/principal";

const en = {
  errorBoundary: {
    title: "Something went wrong",
    description:
      "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.",
  },
  common: {
    preview: "Preview",
    actions: "Actions",
    continue: "Continue",
    next: "Next",
    previous: "Previous",
    complete: "Complete",
    skip: "Skip",
    saveChanges: "Save changes",
    save: "Save",
    saveAndExit: "Save & Exit",
    autoSave: "Auto Save",
    autosaveTemplateName: "Autosave Template",
    templateTitleRequired: "Please enter a Survey Title for the template",
    saveConfirmation: {
      title: "Save changes",
      body: "How would you like to save your changes?",
    },
    upload: "Upload image",
    uploadVideo: "Upload video",
    loading: "Loading",
    back: "Back",
    cancel: "Cancel",
    delete: "Delete",
    close: "Close",
    twentyMinutes: "20 min",
    fiftyMinutes: "50 min",
    savedSuccessfully: "Saved successfully.",
    errorOccurred: "Something went wrong. Please try again.",
    resetPasswordError: "Error occurred while attempting to reset password",
    notFoundEmailError:
      "Configured or registered user with this email not found",
    email: "Email address",
    password: "Password",
    passwordConfirm: "Confirm your password",
    corporateEmail: "Please enter your corporate email address",
    anyEmail: "Enter your corporate email address",
    today: "Today",
    scheduleSession: "Schedule a session",
    instantVideo: "Instant call",
    invalidEmail: "Invalid email address",
    invalidDate: "Please enter a valid date.",
    invalidPasswordLength: "8 characters long",
    invalidPasswordMatch: "1 uppercase & 1 lowercase character & 1 number",
    invalidPasswordConfirm: "Passwords don’t match",
    requiredField: "This field is required",
    errorOccurred: "Something went wrong. Please try again.",
    no: "No",
    checkSearch: "Add a new one or check if the search is correct",
    noFound: "No {{name}} found",
    switchAccountError:
      "Error occurred while attempting to switch organization",
    dragNDrop: "Drag and drop some files here, or click to select files",
    continueWithCode: "Continue with code",
    continueWithoutCode: "Skip",
    areYouSure: "Are you sure?",
    yes: "Yes",
    create: "Create",
    continue: "Continue",
    keywordSearch: "Search",
    filteredBy: "Filtered by",
    selectAll: "All",
    none: "None",
    matchAll: "and",
    matchAny: "or",
    itemsSelected: "{0} selected",
    watchHelloVideo: "Watch greeting video",
    helloVideoTitle: "Greeting video",
    profileImage: "Profile image",
    aboutMeVideo: "Greeting video",
    notes: "Notes",
    noAnswer: "No answer found",
    noAvailableOptions: "No available options",
    noRecordsFound: "No records found",
    noRecordsFoundSubtitle:
      "Try adjusting your search or filters to find what you're looking for.",
    notEligibleToChange: "Not eligible to change",
    notEligibleToSendEmail: "Not eligible to send email",
    notEligibleToDeleteUser: "Not eligible to delete user",
    notEligibleToDeactivateUser: "Not eligible to deactivate user",
    notEligibleToChangeAttribute: "Not eligible to change attribute",
    add: "Add",
    color: "Color",
    colorOptions: {
      red: "Red",
      yellow: "Yellow",
      green: "Green",
      normal: "Normal",
    },
    min: "min",
    done: "Done",
    send: "Send",
    selectStatus: "Select a status",
    showMore: "Show more",
    showLess: "Show less",
    download: "Download",
    downloadExcel: "Download EXCEL",
    downloadPdf: "Download PDF",
    downloadQrAsSvg: "Click and download QR as SVG",
    copied: "Copied",
    copyError: "Error while copying",
    ok: "Okay",
    updateAvailable: "Update available",
    updateAvailableDescription:
      "A new version of Allbry is available. Please refresh the page to get the latest version.",
    refresh: "Refresh",
    copyToClipboard: "Copy to clipboard",
    copied: "Copied",
    backNavigationMessage:
      "Are you sure you want to go back? If you go back, your data will be lost.",
    yesBack: "Yes, go back",
    saveAndGoBackMessage: "Are you sure you want to save and go back?",
    saveAndGoBack: "Yes save & go back",
    unsavedChangesMessage:
      "You have unsaved changes. Do you want to save them before going back?",
    saveAndGoBackButton: "Save & Go Back",
    discard: "Discard",
    goToAccountConfirmMessage:
      "Are you sure you want to switch to the {{name}}? If you proceed, any unsaved data will be lost.",
    yesSwitch: "Yes, switch",
    current: "Current",
    system: "System",
    manual: "Manual",
    downloadPDF: "Download PDF",
  },
  process: {
    title: "Processes",
    all: "All processes",
    active: "Active processes",
    completed: "Completed processes",
    pending: "Pending processes",
    create: "Create process",
    search: "Search processes...",
    noProcesses: "No processes yet",
    noProcessesSubtitle:
      "Create and manage structured processes to track student progress, tasks, and milestones all in one place.",
    noProcessesCta: "Create your first process",
    sortBy: "Sort by",
    sortByStudent: "Student",
    sortByProcessType: "Process type",
    sortByStartedDate: "Started date",
    sortByNextTask: "Next task date",
    sortByEndDate: "End date",
    sortByStudents: "Students count",
    sortByStudentName: "Student name",
    sortByTaskName: "Task name",
    sortByProcessName: "Process name",
    searchPlaceholder: "Search by student, task or process name...",
    chooseStudentsTitle: "Which students do you want to add?",
    nextTask: "Next task",
    started: "Started",
    late: "Late",
    upcomingSoon: "Upcoming",
    studentsCount_one: "{{count}} student",
    studentsCount_other: "{{count}} students",
    studentsTab: "Students",
    attributesTab: "Attributes",
    ownedByMe: "Owned by me",
    studentCases: "Student cases",
    programs: "Programs",
    investigations: "Investigations",
    taskDeadline: "Task deadline",
    viaAttribute: "Via attribute",
    createdFromAttribute: "This process was created from attribute",

    tabs: {
      overview: "Overview",
      events: "Events",
      information: "Information",
    },
    tasks: "Tasks",
    attachments: "Attachments",
    processTimeline: "Investigation process",
    done: "Done",
    cancelled: "Cancelled",
    skipped: "Skipped",
    bookNow: "Book now",
    consentRequired: "Consent required",
    currentStep: "Current step",
    createNow: "Create now",
    registerScreening: "Register screening",
    registerMeasurement: "Register measurement",
    createReferral: "Create referral",
    createConsent: "Create consent",
    skipTask: "Skip",
    requireSkipWarning:
      "This step is required for this process. If you skip it, you will not be able to fill in the information for this step.",
    skipTaskConfirm:
      "Are you sure you want to skip this step? If you skip it, you will not be able to fill in the information for this step.",
    completeTaskConfirm:
      "Are you sure you have reviewed the relevant information? This may include student profile warnings or unread chats.",
    export: "Export",
    addReport: "Add report",
    processName: "Process",
    student: "Student",
    noTasks: "No completed tasks",
    noTasksHint: "Completed tasks from this process will appear here",
    noAttachments: "No attachments",
    noAttachmentsHint: "Uploaded files and documents will appear here",
    info: {
      overviewSection: "Overview",
      studentsSection: "Students",
      progressSection: "Progress",
      stepsSection: "Process Steps",
      startDate: "Start Date",
      endDate: "End Date",
      journal: "Journal",
      reason: "Reason",
      noReason: "No reason provided",
      notSet: "Not set",
      totalTasks: "Total tasks",
      tasksHandled: "Tasks handled",
      completedTasks: "Completed",
      skippedTasks: "Skipped",
      remainingTasks: "Remaining",
      access: "Access",
    },
    noEvents: "No events yet",
    noEventsHint:
      "Events from this process will appear here once tasks are started or completed",
    eventActor: "By {{name}}",
    eventSingular: "event",
    eventPlural: "events",
    pdf: {
      exported: "Exported",
      status: "Status",
      taskName: "Task",
      taskType: "Type",
      taskDate: "Date",
      taskBy: "By",
      eventDate: "Date & Time",
      eventType: "Event",
      eventTask: "Task",
      eventBy: "By",
      attachmentName: "File Name",
      attachmentDescription: "Description",
      attachmentDate: "Uploaded",
      attachmentBy: "By",
      attachmentView: "View",
      studentName: "Name",
      studentEmail: "Email",
      studentGender: "Gender",
      studentExternalId: "External ID",
      studentAttribute: "Attributes",
    },
    taskTypes: {
      appointment: "Appointment",
      customReport: "Custom report",
      note: "Note",
      reminder: "Reminder",
      survey: "Survey",
      consent: "Consent",
      manual: "Manual",
      custom: "Custom",
      information: "Information",
      visionScreening: "Vision screening",
      hearingScreening: "Hearing screening",
      growthMeasurement: "Growth measurement",
      referral: "Referral",
    },
    statusValues: {
      active: "Active",
      completed: "Completed",
      cancelled: "Cancelled",
    },

    stepper: {
      step1: "Category",
      step2: "Template",
      step3: "Students / Attributes",
      step4: "Journal",
      step5: "Privacy",
      step6: "Review",
    },
    step1: {
      title: "Case Type",
      subtitle: "Choose the type of process you want to start",
    },
    step2: {
      noTemplates: "No templates available for this category",
      noAccess:
        "You don't have access to create this process.Please request journal access.",
      subtitle: "Select a template that fits your needs",
    },
    step3: {
      title: "Students / Attributes",
      subtitle: "Add students directly, or filter by a shared attribute",
      studentsSection: "Students",
      attributesSection: "Attributes",
      addStudents: "Add students",
      addStudentsHint: "Click to browse and select students",
      editStudents: "Edit selection",
      addAttributes: "Add by attribute",
      addAttributesHint: "Filter students by a shared attribute",
      editAttributes: "Edit selection",
      noAttributes: "No attributes found",
      selectedAttributes: "Selected Attributes",
      noAttributesSelected: "No attributes selected",
      statsUniqueViaAttributes: "Unique students from attributes",
      statsSharedAcrossAttributes: "Matched by multiple attributes",
      statsAlreadySelectedManually: "Already selected manually",
      statsAlsoMatchedByAttributes: "Also matched by attributes",
      statsTotalUnique: "Total unique students",
      statsEstimatedMatches: "Estimated matches",
      statsEstimatedMatchesSummary_one:
        "About {{count}} student matched before removing overlap",
      statsEstimatedMatchesSummary_other:
        "About {{count}} students matched before removing overlap",
      summaryTitle: "Process summary",
      summaryTargetStudents_one:
        "This process will be created for {{count}} student",
      summaryTargetStudents_other:
        "This process will be created for {{count}} students",
      summaryTargetStudentsEmpty:
        "Select students or attributes to see the final target count",
      summarySelectedManually: "Selected manually",
      summaryMatchedByAttributes: "Matched by attributes",
      summaryOverlapRemoved: "Overlap removed",
      summaryFinalStudents: "Final students",
      statsAttributes: "attribute",
      statsAttributesPlural: "attributes",
    },
    step4: {
      title: "Select Journal",
      subtitle: "Choose the journal type for documentation",
    },
    step5: {
      title: "Privacy settings",
      subtitle: "Control who can access this process.",
      publicLabel: "Shared with journal counsellors",
      publicDescription:
        "All counsellors who have access to the selected journal type can view and manage this process.",
      privateLabel: "Private process",
      privateDescription:
        "Only you and the counsellors you choose below will have access.",
      makePrivate: "Make this process private",
      accessTitle: "Who has access",
      searchCounsellors: "Search counsellors...",
      noCounsellors: "No counsellors found.",
      youOwner: "You (owner)",
      summaryPublic: "Shared with journal counsellors",
      summaryPrivate: "Private — you + {{count}} counsellor(s)",
      summaryPrivateOnlyYou: "Private — only you",
    },
    step6: {
      defaultTemplateName: "Process Template",
      reviewTitle: "Review & Confirm",
      reviewSubtitle: "Check your selections before creating the process",
      summaryCategory: "Category",
      summaryTemplate: "Template",
      summaryStudents: "Students",
      summaryJournal: "Journal",
      summaryAccess: "Access",
      targetingTitle: "Target students",
      reasonLabel: "Reason for the process",
      reasonPlaceholder: "Describe the reason for starting this process...",
      processTimeline: "Follow these steps",
      noSteps: "No steps available for this template",
      addStep: "Add step",
      finalStep: "Final step",
      required: "Required",
      dueInDays: "Due in {{days}} days",
      attachConfig: "Attach report",
      selectConfig: "Select a report config",
      searchConfigs: "Search configs...",
      noConfigs: "No configs found",
      createConfig: "Create config",
    },
    errors: {
      categoryRequired: "Category is required",
      templateRequired: "Template is required",
      studentsRequired: "At least one student is required",
      journalTypeRequired: "Journal type is required",
      reasonRequired: "Reason is required",
      fetchTemplatesFailed: "Failed to fetch templates",
      createFailed: "Failed to create process",
    },
    bulk: {
      viewBulk: "View bulk overview",
      caseCount_one: "{{count}} case",
      caseCount_other: "{{count}} cases",
      sortAscending: "ascending order",
      stepPrefix: "Step {{number}}: {{name}}",
      overallProgress: "Overall progress",
      stepsCompleted: "{{done}}/{{total}} steps done",
      showAll: "Show all {{count}} →",
      addStep: "Add step",
      notCompleted: "Not completed",
      overlayCompleted: "{{done}}/{{total}} completed",
      sendReminder: "Send reminder",
      summary: "Summary",
      note: "Note",
      doneCount: "{{count}} done",
      remainingCount: "{{count}} remaining",
      notStartedCount: "{{count}} not started",
      stepsHeading: "Steps",
      taskCompleted: "Task completed",
      taskSkipped: "Task skipped",
      taskNotCompleted: "This task is not yet completed for this student.",
      viewProfile: "View student profile",
      viewReferralDetails: "View referral details",
      viewConsentDetails: "View consent details",
      viewProcessDetail: "View process detail",
      due: "Due",
      overdue: "Overdue",
      dueSoon: "Due soon",
      upcoming: "Upcoming",
      completed: "Completed",
      studentCount_one: "{{count}} student",
      studentCount_other: "{{count}} students",
      tasksDoneOf: "{{done}}/{{total}} tasks done",
      exportTitle: "Bulk Process Export",
      stepsOverview: "Steps Overview",
      studentCasesTable: "Student Cases",
      stepProgress: "{{done}} / {{total}} done",
      attributesSection: "Attributes",
      noAttributes: "No attributes",
      pdfDoneTotal: "Done / Total",
      pdfProgress: "Progress",
      pdfCurrentStep: "Current step",
      customTasks: "Custom tasks",
    },
  },
  modal: {
    sizeLimit: {
      title: "File size limit has been reached",
      content:
        "File is too big for the upload, the limit is {{sizeLimit}}. Please, choose another one.",
    },
    fileType: {
      title: "Invalid file type",
      content: "You are only able to use images",
    },
    akkImage: {
      akkImage: "AAC image",
      customImage: "Custom image",
      uploadImage: "Select image",
    },
    endDate: {
      title: "End date",
      content: "Please select the end date",
    },
  },
  mobile: {
    notAvailable: "Allbry web version is not available on mobile",
    availableInLandscape:
      "Allbry web version is only available in landscape mode",
    pleaseUseLinks: "Please, download our mobile app using the link below",
    pleaseUseLinksLandscape:
      "You can download our mobile app using the link below",
    appStore: "App Store",
    googlePlay: "Google Play",
  },
  navigationGuide: {
    title: "Quick guide",
    step: "Step {{current}} of {{total}}",
    profileMenu: "Profile menu",
    availability: "Online status",
    defaultDescription: "Open {{title}} from this menu to use its features.",
    descriptions: {
      appointments: "View your appointments and schedule a new meeting here.",
      messages: "Read and send messages to stay in touch and get support.",
      checkInsAndSurveys: "Complete your check-ins and surveys and view your responses here.",
      students: "Find students and open their profiles, messages and support information.",
      users: "View and manage users and their information.",
      reports: "View and manage reports here.",
      customReports: "Create and manage report templates and reports.",
      statistics: "View statistics and follow up on activity and results.",
      emailStatistics: "View email delivery statistics and results.",
      organization: "View and manage your organization.",
      organizations: "View and manage organizations.",
      counsellors: "Find student health staff and view their profiles.",
      config: "Create and manage templates for check-ins and surveys.",
      process: "Create and follow support processes and their activities.",
      broadcastMessages: "Send messages to multiple recipients.",
      dataMigration: "Import and manage organization data.",
      ftpDataCollection: "Manage data collected through FTP.",
      configForm: "Configure forms and their fields.",
      superActions: "Access administrative tools and actions.",
      profileMenu: "Select your profile icon to open your profile, settings and account options.",
      availability: "Switch your status between online and offline.",
      profile: "View your profile and update your personal information and profile picture.",
      notifications: "View your notifications and unread updates.",
      settings: "Manage your preferences and account settings.",
      logOut: "Select this option when you want to log out of Allbry.",
    },
  },
  introduction: {
    getSupport: "Get support from Allbry, wherever you are",
    chat: {
      student: "Jamie",
      counsellor: "Margo",
      messages: {
        first: "She said I was lazy!",
        second: "I have school, dance class, the horses and tutoring!",
        third: "She doesn't get me... 😔",
        fourth: "Oh dear, let's talk!",
      },
    },
  },
  auth: {
    login: {
      withEmail: "Enter your email address",
      codeStepTitle: "Did you receive an registration code?",
      loginSuccess: "You have logged in successfully",
      enterPassword: "Enter your password",
      codeStepSubtitle:
        "In some cases you need a registration code to sign up. If you received one, please enter it below. If not, just press skip.",
      codeInput: "Registration code",
      codeInputHint: "Please enter your registration code if you received one",
      createPass: "Create your password",
      terms: "I have read, understood and hereby approve of Allbrys ",
      termsLink: "terms of use",
      paymentTitle: "Renew your subscription",
      paymentDescription:
        "Allbry costs {{price}} {{currency}} every month. The subscription is active until you cancel it.",
      rememberMe: "Remember me",
    },
    logout: {
      logoutSuccess: "You have logged out successfully",
    },
    signUpConsumer: {
      terms: "I have read, understood and hereby approve of Allbrys ",
      termsLink: "terms of use",
      startJourney: "Start your journey with Allbry today",
      withOrganization: "Sign up with Organization",
      withEmail: "Sign up with Email",
      signUp: "Sign up",
      createPass: "Create your password",
      haveAccount: "Already have an account?",
      logIn: "Log in",
      connectedOrganization: "Your account belongs to ",
      aboutYourSelfHeader: "Choose your username",
      aboutYourSelfDescription1:
        "Choose the name you want to be visible to the counsellor or advisor.",
      aboutYourSelfDescription2:
        "We do not disclose your personal information to third parties.",
      paymentTitle: "Create your subscription",
      paymentDescription:
        "Allbry costs {{price}} {{currency}} every month. The subscription is active until you cancel it.",
      paymentDescriptionSuccess:
        "Congratulations! Your account is now successfully created and your subscription is active until you cancel it.",
      successTitle: "Congratulations!",
      successDescription:
        "Welcome to Allbry! We are here to support, motivate and guide you.",
      klarnaLoadingError:
        "Something went wrong when trying to load the payment widget, please try again",
      klarnaOrderError:
        "Something went wrong when trying to complete the payment, please try again",
    },
    forgot: {
      checkStep: "Check your email",
      weJustSent: "We've just sent",
      resetPassLink: "reset password link",
      followInstructions:
        "to your email. Please check your inbox and follow the instructions.",
      enterEmail: "Enter your email",
      createNewPass: "Create new password",
    },
    registrationError: "User registration failed",
    signInError: "Wrong email or password",
    invalidPassword: "Invalid password",
    forgotPassword: "Forgot password?",
    externalJoin: {
      title: "Did you receive an meeting code?",
      description:
        "If you received a meeting code, please enter it below to get started.",
      placeholder: "Enter your meeting code",
      help: "If you don't have a meeting code, please contact your organizer.",
      invitationMessage: "You have been invited to a meeting in Allbry",
      invitationDescription:
        "You have been invited to a meeting through Allbry by {{counselorName}}",
      information: "Information {{description}}",
    },
  },
  navbar: {
    switch: "Set yourself as",
    toOffline: "Offline",
    toOnline: "Online",
    appointments: {
      title: "Home",
      text: "Schedule meetings with student health staff to get more qualified help",
    },
    messages: {
      title: "Messages",
      text: "Communicate with student health staff and get help anytime",
    },
    students: {
      title: "Students",
    },
    referrals: {
      title: "Referrals",
    },
    consents: {
      title: "Consents",
    },
    reports: {
      title: "Reports",
    },
    organizations: {
      title: "Organizations",
    },
    statistics: {
      title: "Statistics",
    },
    emailStatistics: {
      title: "Email Statistics",
    },
    profile: "Profile",
    installApp: "Allbry on your phone",
    installAppSubtitle: "A simpler way to stay connected",
    clickHere: "Get app",
    settings: {
      title: "Settings",
    },
    logOut: "Log out",
    [PRINCIPAL_ROUTE_NAME.ORGANIZATION]: {
      title: "organization",
    },
    [PRINCIPAL_ROUTE_NAME.STATISTICS]: {
      title: "statistics",
    },
    [PRINCIPAL_ROUTE_NAME.USERS]: {
      title: "users",
    },
    counsellors: {
      title: "Student health staff",
    },
    ftpDataCollection: {
      title: "FTP Data Collection",
    },
    dataMigration: {
      title: "Data Migration",
    },
    configForm: {
      title: "Config Form",
    },
    notifications: {
      title: "Notifications",
      empty: "No notifications yet",
      today: "Today",
      yesterday: "Yesterday",
      all: "All",
      unread: "Unread",
      allCaughtUp: "You're all caught up.",
      justNow: "Just now",
      minutesAgo: "{{count}} min ago",
    },
    superActions: {
      title: "Super Actions",
    },
    checkInsAndSurveys: {
      title: "CheckIns and Surveys",
    },
    config: {
      title: "Template",
    },
    broadcastMessages: {
      title: "Broadcast Messages",
    },
    customReports: {
      title: "Reports",
    },
    process: {
      title: "Process",
    },
    healthcareStructure: {
      title: "Healthcare structure",
    },
  },
  notFound: {
    code: "404",
    text: "Not Found",
  },
  settings: {
    title: "Settings",
    billing: {
      title: "Billing details",
      activeStatus:
        'Your subscription is <b class="text-green">Active</b> and you next billing will occur on <b>{{date}}</b>',
      inactiveStatus:
        'Your subscription is <b  class="text-red">Inactive</b>, but you account is available until <b>{{date}}</b>',
      manageSubBtn: "Manage subscription",
      cancelSubBtn: "Cancel subscription",
      manageSub: "Manage subscription",
      cancelSub: "Cancel subscription",
      cancelSubSubtitle:
        "Are you sure you want to leave us? We are always here for support. <br>You are of course welcome back anytime! Your account will remain active until {{date}}, then we say goodbye.",
      confirmCancel: "Confirm cancellation",
    },
    anonymous: {
      title: "Anonymous mode",
      description:
        "Your organization allows you to be anonymous. Your name and profile image will be hidden from other users.",
    },
    allowEmailNotification: {
      title: "Email notification",
      description: "You can turn off email notifications from Allbry",
    },
    prorenataIntegration: {
      title: "Prorenata integration",
      description:
        "Activate the integration to enable Prorenata workflows directly from Allbry.",
      button: "Manage",
      statusConnected: "Connected",
      statusNotConnected: "Not yet connected",
      toggleSuccess: "Prorenata status updated.",
      toggleError: "Unable to update Prorenata status.",
    },
    prorenata: {
      configureModal: {
        title: "Configure Prorenata",
        confirm: "Confirm",
        verifyTitle: "Is this you?",
        selectJournalTitle: "Choose primary journal",
        nameLabel: "Name",
        personalNumberLabel: "Personal identity number",
        initializationError: "We could not load your Prorenata details.",
        noJournalAccess:
          "You currently have no access to any journals in Prorenata.",
        createSuccess: "Connection request sent to Prorenata.",
        createError: "Could not create the Prorenata connection.",
        successTitle: "You're all set!",
        successDescription:
          "Next time you create a note or report you can sync it to Prorenata in the final step before finishing.",
      },
    },
    calendarSync: {
      title: "External calendar sync",
      description: "Activate external calendar sync for appointments",
      freeDescription:
        "Activate external calendar sync for added available time",
      freeNote:
        "Note: Most of the time everything is added automatically, but if not, follow the instructions in the email sent to you when adding available times.",
    },
    dataReset: {
      recurrentReset: {
        title: "Recurrent data reset",
        disabledResetParagraph:
          "Enable recurrent data reset to clear data automatically. Please note that data reset affects chats, appointments, notes.",
        enabledResetParagraph:
          "Estimated date for the next reset is {{date}}. Please note that data reset affect chats, appointments, notes.",
        intervalLabel: "Interval",
        intervals: {
          eachMonth: "Each month",
          eachThreeMonths: "Each 3 months",
          eachSixMonths: "Each 6 months",
        },
        errorGettingResetState:
          "Error while getting current recurrent jobs state",
        errorSwitchingResetState: "Error while changing recurrent jobs state",
        errorIntervalChange: "Error while changing reset interval",
        successIntervalChange: "New reset interval has been set",
      },
      manualReset: {
        title: "Reset data now",
        paragraph:
          "Please note that data reset affects chats, appointments, notes.",
        resetButton: "Clear all data",
        resetSuccess: "The data has been cleared",
        resetFailure: "Error while resetting data",
        resetModal: {
          title: "Clear all data",
          paragraph:
            "Are you sure you want to reset all information in chats, appointments, notes?",
          yes: "Yes",
          no: "No",
        },
      },
    },
    policy: {
      title: "Integrity Policy",
      description: {
        firstPart:
          "Being fully aware that our work as consultants and engineers is critical to the achievement of a sustainable development of the society and the environment, we have designed and implemented a range of measures aiming at making our ethical conduct one of IRD Engineering main characteristics.",
        secondPart:
          "All these, and any future measures, are and will be incorporated in our Integrated Management System, which represents the official Company policy and procedures to ensure that the Company's services are carried out at a high professional level, consistent with the highest international standards in terms of quality assurance, environmental sustainability and worker's health and safety.",
      },
      policyCopy:
        "A copy of our Code of Ethics and Integrity Policy can be found",
      hereLink: "here",
    },
    tutorials: {
      title: "Tutorials",
      description: "Learn how to use Allbry",
      hereLink: "here",
    },
    language: {
      title: "Language",
    },
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm New Password",
    passwordChanged: "Password Changed Successfully",
    unavailable: {
      title: "Unavailable",
      description: "Enter the field below to set your unavailable message",
      success: "Your unavailable massage has been updated successfully",
      unavailableText: "Write your unavailable text here",
      anywaySendMessage:
        "Allow the student to continue sending messages even if you are unavailable.",
    },
    onlineStatus: {
      updated: "Status updated successfully.",
      error: "Error updating status.",
    },
  },
  profile: {
    personProfile: "{{type}}'s profile",
    personProfileName: "{{person}}'s profile",
    overview: "Profile Overview",
    edit: {
      title: "Edit profile",
      restriction: "Only owners and admins can edit.",
      gender: {
        male: "Male",
        female: "Female",
        other: "Other",
        private: "Do not wish to say",
      },
      about: "About",
      changesSaved: "Changes Saved",
    },
    personalInfo: "PERSONAL INFORMATION",
    contactInfo: "CONTACT INFORMATION",
    personAttributes: "ATTRIBUTES",
    about: "About me",
    name: "Name",
    selectRole: "Select role",
    birthday: "Date of Birth",
    fullBirthday: "{{birthday}} ({{fullYears}} years)",
    appointments: "APPOINTMENTS",
    noAppointments: "No appointments found for this category",
    gender: "Gender",
    age: "Age",
    email: "Email",
    lastNote: "{{person}}'s last note will be placed here.",
    parentAccountID: "Parent account ID",
    noParentIfEmpty: "No parent if empty",
    secretIdentity: "Protected identity",
    createReport: "Create report",
    studentReport: "Student report",
    attributeHistory: "Attribute History",
    noAttributeHistory: "No history found",
  },
  appointments: {
    title: "Appointment",
    session: {
      today: "Join Now",
      future: "Cancel or Reschedule",
      completed: "Book another session",
      cancelled: "Book another session",
      cancel: "Cancel",
    },
    status: {
      upcoming: "Upcoming",
      completed: "Completed",
      cancelled: "Cancelled",
      pending: "Pending confirmation from you",
      unanswered: "Pending confirmation by {{name}}",
      active: "Accepted",
    },
    action: {
      accept: "Accept",
      reschedule: "Reschedule",
      deny: "Deny",
      goToAppointment: "Go to appointment",
    },
    unansweredDialog: {
      title: "You have unanswered appointment requests",
      continueBtn: "Continue later",
    },
    booking: {
      title: {
        booking: "Booking",
        reschedule: "Reschedule",
      },
      commentAndDescription: "Comment/description",
      descriptionText:
        "Add a comment or description to your appointment that will be visible to the other attendees",
      descriptionPlaceholder: "Enter description",
      descriptionMaxLengthWarning: "You can add a maximum of 250 characters",
      schedule: "Schedule",
      chooseUser: "Choose users",
      selectUser: "Select user",
      chooseStudent: "Choose student",
      selectStudent: "Select student",
      sessionDuration: "Choose session duration",
      sessionTime: "Choose session time",
      success: "Success",
      successText:
        "The session was successfully {{action}}! Now you can check your session in upcoming appointments",
      successBooked: "booked",
      successRescheduled: "rescheduled",
      back: "Back to appointments",
      chooseStudentModal: "Choose student",
      chooseUserModal: "Choose users",
      done: "Done",
      chooseCounsellor: "Choose student health staff",
      chooseCounsellorModal: "Choose student health staff",
      selectCounsellor: "Select student health staff",
      searchStudents: "Search students",
      noTimeSlots: "No available time slots",
      recommendedSlotsMassage:
        "No available slots, choose a different date or select on of the recommended slots below",
      recommendedSlots: "Recommended slots",
      custom: "Custom time",
      startTime: "Start time",
      endTime: "End time",
      video: "Video",
      students: "Students",
      counsellors: "Student health staff",
      search: {
        student: "Search student",
        counsellor: "Search health staff",
      },
      notFound: {
        student: "No student found",
        counsellor: "No student health staff found",
      },
      selectExternal: "Add external participant",
      name: "Name",
      number: "Phone number",
      email: "Email",
      participantLimitReached:
        "You can add up to {{count}} participants, including external participants. Remove one to add another.",
    },
    detail: {
      scheduleInfoCancelOrReschedule:
        "The session is scheduled on {{date}} at {{time}}. You're able to cancel or reschedule it.",
      scheduleInfoBookAnother:
        "The session is scheduled on {{date}} at {{start}} - {{end}}. You are able to book another one.",
      reschedule: "Reschedule",
      cancel: "Cancel Appointment",
      areYouSure: "Are you sure you want to cancel your session?",
      yes: "Yes, Cancel",
      cancelled: "The session was cancelled",
      sessionDetails: "Session details",
      externalUserInfo: "External user info",
      copied: "Copied",
      copyError: "Error while copying",
      resendEmailToExternalUser:
        "Are you sure you want to resend the email to the external user?",
      description: "Description",
    },
    now: "Now",
    scheduleNew: "Schedule a new appointment",
    counsellors: "Student health staff",
    appointments: "Appointments",
    activities: "Activities",
    addNew: "Create",
    seeAll: "See all",
    notExist: "Appointment does not exist",
    weeksInterval: "In {{interval}} weeks",
    daysInterval: "In {{interval}} days",
    dayInterval: "In {{interval}} day",
    hoursInterval: "In {{interval}} hours",
    hourInterval: "In {{interval}} hour",
    minutesInterval: "In {{interval}} minutes",
    minuteInterval: "In {{interval}} minute",
    today: "Today",
    future: "Future",
    completed: "Completed",
    cancelled: "Cancelled",
    noAppointments: "No appointments here yet...",
    organizationNotWorking: "Organization is not working on this day",
    scheduleFirst: "Schedule your first appointment",
    yourFreeTime: "Your available time",
    freeTime: {
      title: "Your available time",
      addTime: "Add time",
      noFreeTimes:
        "Please allocate your available time in order students can book the appointments",
      allocateTime: "Allocate available time",
      editTime: "Edit available time",
      startTime: "Start time",
      endTime: "End time",
      add: "Add",
      cancel: "Cancel",
      date: "Date",
      ends: "Ends",
      orgNotWorking: "Organization is not working",
      repeats: {
        everyday: "Everyday",
        weeklyOn: "Weekly on {{day}}",
        weekdays: "Every weekday (Monday to Friday)",
        none: "Doesn't repeat",
      },
      deleteModal: {
        title: "Delete available time",
        description: "Are you sure you want to delete your available time?",
        delete: "Yes, Delete",
      },
      timeAdded: "Your available time was added",
      timeEdited: "Your available time was edited",
      timeDeleted: "Your available time was deleted",
    },
    customMeeting: "Choose appointment type",
    error: {
      studentOrCounsellor: "Student or staff unavailable between {{data}}",
      and: "and",
      counsellor: "Healthcare staff unavailable this time",
      timeError: "End-time must be greater than start-time",
      minimumTime: "Appointment must be greater than {{minimumTime}} minutes",
      currentTime: "Start-time must be greater than current-time",
      outsideWorkingHours:
        "Selected time is outside the organization's working hours",
      noAvailableTime: "Student or staff unavailable at this time",
    },
    invited: "{{count}} Invited",
    participants: "{{count}} Participants",
  },
  reminderBoard: {
    todo: "To do",
    inProgress: "In progress",
    done: "Done",
    viewAndEdit: "View and edit",
    createdAt: "Created at:",
    attachedTo: "Attached to:",
    createdBy: "Created by:",
    title: "Title",
    description: "Description",
    dueDate: "Due date",
    personOfInterest: "Person of interest",
    titlePlaceholder: "Enter title here",
    descriptionPlaceholder: "Enter description here",
    personOfInterestPlaceholder: "Select person of interest",
    createReminder: "Create reminder",
    searchStudents: "Search students",
    noResults: "No results",
    editUser: "Edit user",
    selectDueDate: "Select due date",
    chooseStudent: "Choose student",
    addUser: "ADD",
    addNew: "Add",
    archive: "Archive",
    archiveConfirmationTitle: "Are you sure you want to archive reminders?",
    deleteConfirmationTitle: "Are you sure you want to delete reminders?",
    reminders: "Reminders",
    viewArchived: "View archived",
    addUser: "Add",
    todoDescription:
      'Simply drag the reminder to "in progress" with your mouse',
    doneDescription:
      "Done reminders older than 30 days are automatically moved to archive",
  },
  notes: {
    title: "Notes & reports",
    note: "Note",
    report: "Report",
    topics: "Topics",
    meetingType: "Meeting type",
    meetingKind: "Meeting place",
    meetingDuration: "Meeting duration",
    student: "Student",
    generated: "Generated",
    add: "Add note",
    personNotes: "{{person}}'s notes & reports",
    personNote: "{{person}}'s note",
    personReport: "{{person}}'s report",
    personStatistics: "{{person}}'s Statistics",
    create: "Create a note",
    saveText: "Take notes about the student.",
    autoSaveText:
      "Take notes about student. All notes will be saved automatically",
    autoSaveTextDuringVideo:
      "Take notes during the session. All notes will be saved automatically",
    metaAppointment:
      "{{person}} finished appointment with {{student}}, {{date}}",
    metaChat: "{{person}} finished chat with {{student}}, {{date}}",
    createdBy: "{{date}} by {{person}}",
    delete: "Delete note",
    deleteQuestion: "Are you sure you want to delete this note?",
    edit: "Edit note",
    editQuestion: "Are you sure you want to edit this note?",
    yesEdit: "Yes, edit",
    yes: "Yes, delete",
    saved: "The note has been saved",
    deleted: "The note has been deleted",
    worryLevel: "Worry level",
    filter: "Filter",
    notes: "Notes",
    reports: "Reports",
    isPinned: "Pin note",
    prorenataToggle: "Send to Prorenata",
    prorenataSuccess: "Note sent to Prorenata.",
    prorenataError: "Could not send the note to Prorenata.",
    pinnedNotes: "This note is pinned",
    isNotPinned: "This note is not pinned",
    isPublic: "Open",
    isForPublic: "This note is visible for other professions",
    isNotForPublic: "This note is not visible for other professions.",
    isUpdated: "This note has been updated",
    publicLabel: "Open",
    pinnedLabel: "Pinned",
    isForReportPublic: "This report is visible for other professions",
    isReportNotForPublic: "This report is not visible for other professions",
    myReports: "My reports",
    pdf: {
      title: "Note Summary",
      createdBy: "Created by",
      createdAt: "Created at",
      content: "Note content",
      generatedAt: "Generated at",
    },
  },
  videoCall: {
    leaveCallModal: {
      title: "Finishing the appointment",
      paragraph:
        "Already done with the appointment? If so, click End the call option and fill the report. If not, click Leave the call and you'll be able to rejoin it.",
      leaveCall: "Leave the call",
      endCall: "End the call",
    },
    videoAccessModal: {
      title: "Camera or microphone is blocked",
      description_1:
        "Allbry requires access to your camera and microphone. Click the camera blocked icon",
      description_2: "in your browser's address bar.",
      leaveCall: "Leave the call",
    },
    join: "Join",
    notJoin: "Not joined",
  },
  chat: {
    end: "End chat",
    archivedForCounsellor:
      "This chat has been archived by the student. To restart the conversation you just need to write something.",
    endInfoForCounsellor:
      "This chat was ended on {{time}} by you. To restart the conversation you just need to write something.",
    startInfoForCounsellor:
      "This chat was started on {{time}} by you. To start the conversation you just need to write something.",
    chatStarted: "(Chat started)",
    noMessages: "No messages here yet",
    sendFirstMessage: "Send your first message to {{person}}",
    writeMessage: "Write a message...",
    endChatQuestion:
      "Are you sure you want to end the chat? The chat will not be deleted, you can restart it by sending the message.",
    attachmentInfo: "{{fileSize}}, {{date}} at {{time}}",
    sharedFiles: "SHARED FILES",
    noFiles: "No shared files here yet..",
    viewFileInfo: "View file info",
    deleteFile: "Delete file",
    fileDetails: "File details",
    fileName: "File name",
    fileSizeLabel: "File size",
    typeLabel: "Type",
    createdAtLabel: "Created at",
    createdByLabel: "Created by",
    descriptionLabel: "Description",
    informationTitle: "Information",
    noDescription: "No description",
    deleteFileQuestion: 'Are you sure you want to delete "{{fileName}}"?',
    fileDeleted: "File deleted",
    fileDeleteError: "Could not delete file",
    general: "General",
    messages: "Messages",
    chatEnded: "(Chat ended)",
    attachment: "(Attachment)",
    image: "(Image)",
    startTitle: "Who you want to talk with?",
    noStudentsFound: "No students found",
    notFoundLabels: {
      student: "No student found",
      counsellor: "No student health staff found",
      globalCounsellor: "No global student health staff found",
    },
    noStudentsAvailable: "No students are available",
    noStudentsAvailableToChat:
      "There are no available students to start a chat with",
    noCounsellorsAvailable: "No student health staff are available",
    noCounsellorsAvailableToChat:
      "There are no available staff to start a chat with",
    start: "Start chat",
    startGroupChat: "Start group chat",
    nameTheGroupChat: "Name the group chat",
    enterGroupName: "Enter a name for group chat",
    groupName: "Name of group",
    more: "View more",
    selectChat: "Select a chat to start messaging",
    archive: "Archive",
    delete: "Delete chat",
    organizationDoesntWork:
      "Organization doesn't work now. It'll take longer time to receive the answer. We recommend to send messages at {{start}} - {{end}}",
    organizationDoesntWorkToday:
      "Organization is not working today. It'll take longer time to receive the answer",
    endInfoForStudent:
      "This chat was ended on {{time}} by {{person}}. To restart the conversation you just need to write something.",
    startInfoForStudent:
      "This chat was started on {{time}} by {{person}}. To start the conversation you just need to write something.",
    sendAfterWork: "Send message outside working hours",
    workingHours:
      "Working hours of organization: {{start}} - {{end}}. If you want to send message outside working hours, please note that you receive answer in working hours.",
    workingHoursToday:
      "Organization is not working today. If you want to send message outside working hours, please note that you receive answer in working hours.",
    ok: "Ok, got it",
    deleteChatQuestion: "Are you sure you want to delete the chat?",
    archiveChat: "Archive chat",
    unarchiveChat: "Unarchive chat",
    deleted: "Chat deleted",
    archived: "Chat archived",
    unarchived: "Chat unarchived",
    deleteGroup: "Delete group",
    modifyGroup: "Modify group",
    groupAllMembers: "Group Members",
    modifyGroupChat: "Modify group chat",
    editGroupName: "Enter a name for the group chat",
    members: "Participants",
    confirmToDelete: "Are you sure to delete this group?",
    confirmToDeleteMember: "Are you sure to remove selected participant?",
    addNewMember: "Add new participant",
    deleteSelected: "Delete selected participant",
    selectNewMember: "Select new participant",
    selectClose: "Select & close",
    noMembersInGroup:
      "There are no more participants in this group, Do you want to delete this group?",
    groupMembers: "{{value}} participants",
    overview: "Overview",
    notesAndReports: "Notes & reports",
    statistics: "Statistics",
    imageFile: "Image or file",
    akkImage: "AAC image",
    read: "Read",
    akk: {
      selectCategory: "Select Category",
      chooseAkk: "Choose images",
      send: "Send",
      noImage: "No images found",
    },
    all: "All",
    students: "Students",
    counsellors: {
      shortName: "Local",
      fullName: "Student health staff",
    },
    globalCounsellors: {
      shortName: "Global",
      fullName: "Global student health staff",
    },
    searchStudent: "Search student",
    searchCounsellor: "Search health staff",
    searchGlobalCounsellor: "Search global health staff",
    active: "Registered",
    inactive: "Inactive",
    deactivated: "Deactivated",
    successDelete: "Message deleted successfully",
    counsellorUnavailable: "Counsellor unavailable",
    videoCall: "Video Call",
    confirmVideoCall: "Are you sure you want to start a video call?",
    counsellorInactive: "Counsellor no longer works in this organization",
    onlyAllbryCanSendMessage: "Only {{name}} can send messages",
    messagesTitleSuperAdmin: "Messages (Tech releases)",
    showMore: "Show more",
    createReportFor: "Create report for {{date}}",
    conversationHasEnded: "The conversation was finished automatically.",
  },
  reports: {
    createReport: "Create report",
    deleteReport: "Delete report",
    editReport: "Edit report",
    editReportQuestion: "Are you sure you want to edit the report?",
    deleteReportQuestion: "Are you sure you want to delete the report?",
    yesEdit: "Yes, edit",
    yes: "Yes, delete",
    deleted: "The report has been deleted",
    title: "Reporting",
    title2: "Non mandatory step",
    selectDate: "1. Date",
    selectTopics: "2. Select the topic in the conversation",
    concernScale: "3. How worried are you about the student?",
    selectMeetingType: "4. What kind of meeting did you have?",
    selectMeetingKind: "5. How did the meeting occur?",
    noteComment: "{{questionNo}}. Note / Comment",
    feedbackAllbry:
      "{{questionNo}}. Is there anything else you want to be able to map? (optional market research)",
    additionalSupportQuestionTitle:
      "{{questionNo}}. Is the student in need of additional support?",
    participationThanks: {
      title: "Thank you!",
      details:
        "Thank you for providing this information. It's really helpful for us.",
      close: "Close",
    },
    topics: {
      startOfSession: "Start of the session",
      checkIn: "Check-in",
      basicCare: "Basic care",
      identity: "Identity",
      anxiety: "Anxiety",
      stress: "Stress",
      physicalHealth: "Physical health",
      training: "Training",
      crime: "Crime",
      sorrow: "Sorrow",
      socialRelationship: "Social relationship",
      workLife: "Work life",
      shame: "Shame",
      honor: "Honor",
      careFailure: "Child neglect",
      economy: "Economy",
      security: "Security",
      stimulationAndGuidance: "Stimulation and guidance",
      socialOrientation: "Social orientation",
      emotionalAvailability: "Emotional availability",
      love: "Love",
      mentalHealth: "Mental health",
      mentalAbuse: "Mental abuse",
      physicalAbuse: "Physical abuse",
      loveRelations: "Love relations",
      familyRelations: "Family relations",
      sexualAbuse: "Sexual abuse",
      economicalAbuse: "Financial abuse",
      drugUse: "Drug use",
      performanceAnxiety: "Performance anxiety",
      loneliness: "Loneliness",
      grooming: "Grooming",
      school: "School",
      education: "Education",
      selfHarm: "Self-injurious behavior",
      suicidalToughts: "Suicidal thoughts",
      religion: "Religion",
      politics: "Politics",
      sexuality: "Sexuality",
      npfDiagnoses: "NPF diagnoses",
    },
    meetingType: {
      emergencyMeeting: "Emergency meeting",
      dropInMeeting: "Drop-in meeting",
      individualMeeting: "Individual meeting",
      noShowMeeting: "No-show meeting",
      parentMeeting: "Meeting with guardian",
      ehtMeeting: "Meeting with representative from EHT",
      groupMeeting: "Group meeting with students",
      adultMeeting: "Meeting with other adult (i.e. teacher)",
      breakMeeting: "Meeting in hallway or during break",
      adultStudentMeeting:
        "Meeting with student, guardian, principal and / or other school staff",
    },
    meetingKind: {
      allbryMeeting: "Did you book or have the meeting in Allbry?",
      allbryMeetingIfNot: "If not, where did you have the meeting?",
      telephone: "Telephone meeting",
      digital: "Digital meeting",
      physical: "Physical meeting",
    },
    prorenataToggle: {
      title: "Sync to Prorenata",
      description: "Send this report to Prorenata",
      success: "Report synced to Prorenata.",
      error: "Could not sync the report to Prorenata.",
    },
    saveError: "Error saving report. Please try again.",
    addTopic: {
      title: "Add topic",
      name: "Name",
      description: "Description",
      button: "Add new",
      success: "Topic added successfully",
      error: "Error while adding topic",
      expandTab: "Expand",
      collapseTab: "Collapse",
      all: "All",
    },
    worriedLevel: {
      level0: "No worry / contact sought",
      level0_5: "Risk on the way to level 1",
      level1: "Preventive / promotional / informative talk or other talks",
      level1_5: "Risk on the way to level 2",
      level2: "Mild worry – worry that you can manage with the student",
      level2_5: "Risk on the way to level 3",
      level3:
        "Medium concern - contact the school and follow-up after 4 weeks or refer to BUP / psychiatry & other instances",
      level3_5: "Risk on the way to level 4",
      level4: "High concern - ”orosanmälan” or similar",
      level4_5: "Risk on the way to level 5",
      level5:
        "Acute concern - act now, alert the emergency services or similar",
      details0:
        "No concern / contact sought \n\nNo concern at this stage. The student has sought contact or started a conversation.",
      details0_5: "Risk on the way to level 1",
      details1:
        "Preventive / promotional / informative talk or other talks \n\nAt this level, student health staff have had preventive / promotional / informative calls or other types of support calls. For example, about friendships, love, future and free time or goals and dreams and similar themes/topics.\n\nGuided conversations in connection with, for example, the high school application (for elementary school students) or the university application (for high school students).",
      details1_5: "Risk on the way to level 2",
      details2:
        "Mild concern - concern that you can manage with the student \n\nAt this level, student health personnel feel mild concern for the student. This means concern where the counsellor/student health staff and student can talk about topics/themes without having to notify others (duty of confidentiality). This may include conversations about self-esteem, self-confidence, past trauma, relationships and health, and experiences that the student is talking about.\n\nAt this stage of concern, the counselor/student health professional can use their skills, knowledge and conversational methods to work with the student and support at different times and situations. \n\nRelationship building, trust and confidence are important at this level as a student may tell something in confidence to the student health staff that the student does not want to reveal to anyone else but still needs help, an example is low self-esteem, tells about NPF diagnoses , investigations, previous contact with authorities such as social services and BUP or adult psychiatry.\n\nThere is a concern for the student but you as student health staff are there to support and sort out the concern.",
      details2_5: "Risk on the way to level 3",
      details3:
        "Medium concern - contact the school and follow-up after 4 weeks or refer to BUP / psychiatry & other agencies \n\nThis is the first step where student health personnel must act and violate confidentiality (non-disclosure). In this case, it is about contacting the school in case of concerns about the child's health (mental and physical health), school attendance, school results, home environment, ostracism and bullying. Offensive treatment. In the first step, a counselor or mentor is contacted. Possibly a case for EHT/EHM to discuss this student, for example in the case of adaptations, difficulties at school. Follow-up for the student takes place after 4 weeks, the counselor is responsible for follow-up.",
      details3_5: "Risk on the way to level 4",
      details4:
        "High concern - report of concern or similar \n\nBased on the National Board of Health and Welfare's governing document and notification obligation according to SoL 14 ch. 1§ & SkolL 29 ch. 13, obligation to report if you know or suspect that a child is in harm's way. Student health personnel notify the student (and the parents in some cases) and inform them that a report of concern is being made and why it is being made. The school must always be informed. A report of concern is usually made in collaboration with the principal.\n\nThe National Board of Health and Welfare's (2013) definition of the term ”children who get hurt” from Children who get hurt or are at risk of getting hurt. A guide for healthcare and dental care regarding reporting obligations and responsibilities.\n\n”The concept of child harm includes all forms of abuse, neglect and exploitation that lead to actual or potential damage to the child's health or development. It can, for example, refer to children and young people who are exposed to physical or psychological violence, sexual abuse, violations or problems in relation to their family, as well as if they witness violence or live in an environment where violence and threats of violence occur. It also includes children and young people who get hurt because of their own behavior, for example because of substance abuse, crime and other self-destructive behavior. Children who are exposed to threats, violence or other abuse from peers or from others, as well as children with major problems in the school situation caused by a social problem can also be considered to be part of the social service's target group. and exploitation that results in actual or potential harm to the child's health or development. It can, for example, refer to children and young people who are exposed to physical or psychological violence, sexual abuse, violations or problems in relation to their family, as well as if they witness violence or live in an environment where violence and threats of violence occur. It also includes children and young people who get hurt because of their own behavior, for example because of substance abuse, crime and other self-destructive behavior. Children who are exposed to threats, violence or other abuse from peers or from others, as well as children with major problems in the school situation caused by a social problem can also be considered part of the social services target group.”",
      details4_5: "Risk on the way to level 5",
      details5:
        "Urgent concern - act now, alert the emergency services or similar \n\nAct immediately. At this stage, there is immediate danger to the student's life or health, or other situations where action must be taken immediately. Call 112. Contact emergency services. It could be that a student writes that they will attempt suicide, commit a crime or put themselves or someone else in immediate danger. Acute concern - act now, alert the emergency services or the like \n\nAct immediately. At this stage, there is immediate danger to the student's life or health, or other situations where action must be taken immediately. Call 112. Contact emergency services. It could be that a student writes that they will attempt suicide, commit a crime or put themselves or someone else in immediate danger.",
      title: "Information level ",
    },
    types: {
      quick: {
        title: "Quick",
        description: "Only a limited number of subjects",
        selectTopics: "1. Select the topic in the conversation",
        selectMeetingKind: "2. How did the meeting occur?",
      },
      normal: {
        title: "Normal",
        description: "Full length report",
      },
    },
    additionalSupport: {
      youthReception: "Youth reception",
      socialServices: "Social services",
      medicalAndHealthcare: "Medical and healthcare",
    },
    statistics: {
      additionalSupport: "Additional support",
    },
  },
  principal: {
    validation: {
      notEmpty: "Value can't be empty",
      noSpaces: "Value can't have spaces",
      lessThan30Symbols: "{{value}} should be less than 30 symbols",
      maxName: "Name should be less than {{max}} characters",
      exists: "Attribute already exists",
      ageLessThan8: "Your age cannot be less than 8",
      ageLessThan18: "Your age cannot be less than 18",
      required: "{{value}} is required",
      requiredName: "Name is required",
      requiredEmail: "Email is required",
      notValidEmail: "Not valid email",
      roleRequired: "Role is required",
      selectAtLeastOneJournal: "Please select at least one journal type",
    },
    addCustomField: "Add custom field",
    overview: "{{name}} Overview",
    schedule: "Organization schedule",
    addCustomAttributes: "Add custom attributes",
    customFieldsDescription:
      "While creating the user you'll see added custom fields.",
    anonymous:
      "Students in your organization are allowed to be anonymous. Their names and profile images will be hidden from other users.",
    removeChatTitle: "Remove chats",
    removeChatDescription:
      "Students in your organization are allowed to remove chats with student health staff.",
    archiveChatTitle: "Archive chats",
    archiveChatDescription:
      "Students in your organization are allowed to archive chats with student health staff.",
    to: "To",
    from: "From",
    workingHours: "Working hours",
    custom: "Custom",
    weekDays: "Weekdays",
    allDays: "All days",
    addCustomMeetingTypes: "Create custom appointment types",
    addMeetingType: "Create appointment type",
    createMeetingType: "Create appointment type",
    editMeetingType: "Edit appointment type",
    chooseMeetingTypeEN: "Choose name of appointment type in english",
    chooseMeetingTypeSW: "Choose name of appointment type in swedish",
    meetingDescriptionSW:
      "Enter a description for the appointment type in swedish",
    meetingDescriptionEN:
      "Enter a description for the appointment type in english",
    videoEnable: "Video enabled",
    meetingCreated: "Appointment type created successfully",
    errorCreateMeeting: "Error while create appointment type",
    updatedMeeting: "Updated appointment type successfully",
    errorUpdatingMeeting: "Error updating appointment type",
    removeMeeting: "Appointment type removed successfully",
    errorRemovingMeeting: "Error while removing appointment type",
    MeetingNameRequired: "Appointment type name is required",
    confirmRemoveMeeting: "Are you sure to remove appointment type",
    editMeeting: "Edit",
    removePopup: "Are you sure to remove appointment type?",
    video: "Video",
    appointmentTypeDescription:
      "The default Video meeting type will dissapear if a new video meeting type is added",
    sessionDuration: "Select meeting duration time",
    sessionDurationNote:
      "Note: Please make sure you make a selection. Because if no option is selected then the default options are 20 minutes and 50 minutes",
    days: {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
    startLaterThanEnd: "Start time is later than end time",
    endBeforeStart: "The end time is before the start time",
    edit: "Edit User",
    add: "Add User",
    student: "Student",
    counsellor: "Student health staff",
    allbryCounsellor: "Allbry Counsellor",
    userCreated: "The user was created successfully.",
    errorCreatingUser: "Error creating user",
    userUpdated: "The user was updated successfully.",
    errorUpdatingUser: "Error updating user",
    errorUpdatingJournalAccess: "Error updating journal access",
    userTable: {
      viewProfile: "View profile",
      editProfile: "Edit profile",
      users: "users",
      active: "Registered",
      inactive: "Inactive",
      deactivated: "Deactivated",
      nameSurname: "Name Surname",
      birthday: "Date of birth",
      organization: "Organization",
      activate: "Activate",
      deactivate: "Deactivate",
      profileActivated: "Profile activated",
      profileDeactivated: "Profile deactivated",
      activatingError: "Error activating profile",
      deactivatingError: "Error deactivating profile",
      generateCsvTemplate: "Generate csv template",
      generateXlsxTemplate: "Generate xlsx template",
      importUsers: "Import users from xls or xlsx",
      csvImportTitle: "Import is in progress",
      csvImportBody:
        "Importing users may take some time. Please, don't close the window",
      importButton: "Import",
      selectFiles: "Select file to import",
      importTitle: "Import users",
      usersAddSuccess: "Users successfully added",
      usersAddError: "Error in importing users",
      importSummary: "{{created}} created, {{updated}} updated",
      importRolledBack:
        "Import failed: {{failed}} of {{total}} rows invalid. Nothing was imported — fix the listed rows and re-upload.",
      wrongFormat: "File format should be xls or xlsx",
      wrongImportData: "Error in row {{row}}:\n{{error}}",
      validationError: "{{field}} ✑ {{error}}",
      onboardingEmailDialogInstruction:
        "You will send onboarding emails to the following users",
      inActivateMultipleUserDialogInstruction:
        "You will deactivate the following users",
      scrapeMultipleUserDialogInstruction:
        "You will scrape the following users",
      sendEmail: "Send Email",
      onboardingEmail: "Onboarding Email",
      customEmail: "Custom Email",
      disableStudent: "Disable Students",
      disableUser: "Disable Users",
      actions: "Actions",
      role: "Role",
      class: "Class",
      usersDelete: "Delete users",
      requestScrapeSuccess:
        "Request for Scrape Multiple Users submitted successfully",
      changeAttributesSuccessMessage: "Attributes changed successfully",
      changeAttributes: "Change attributes",
      deactivatedSuccessMessage: "Users deactivated successfully",
      importDescription:
        "You can add a description for the import process here.",
      importDescriptionPlaceholder: "Enter description here...",
    },
    csv: {
      custom: "Fill custom attribute",
      email: "Fill person's email",
      role: "Fill number: 1 - student, 2 - counsellor",
      gender: "Fill number: 1 - male, 2 - female, 3 - other",
      year: "Year of birth",
      month: "Month of birth (number)",
      day: "Day of birth",
      nameSurname: "Fill person's name and surname",
      dateOfBirth: "Date of birth: yyyymmdd",
      fillNumber: "Fill number",
      workSheetName: "Sheet1",
      externalId: "Fill person's external id (optional)",
    },
    csvFields: {
      custom: "Custom attribute",
      email: "Email",
      role: "Role",
      gender: "Gender",
      year: "Year of birth",
      month: "Month of birth",
      day: "Day of birth",
      nameSurname: "Name and surname",
      dateOfBirth: "Date of birth",
      fillNumber: "Fill number",
      workSheetName: "Sheet1",
      attribute: "Attribute",
      externalId: "External id",
    },
    csvFieldsValidationMessages: {
      dateOfBirth: "Date of birth must be in the format yyyymmdd",
      attributeRequired: "{{attributes}} is required",
      nameSurnameInvalid: "Name is invalid",
      roleInvalid: "Role {{role}} is invalid",
      roleNotPermitted:
        "You do not have permission to import users with the Principal role",
    },
    usersOrganization: "{{name}} Users",
    addUserButton: "Add user",
    about: "About me",
    user: {
      role: "Role",
      email: "Email",
      status: "Status",
      forcePassword: "Force Password",
      attribute: "Attribute",
      attributeDescription:
        "You can select multiple attributes here. \n\nStudents with atleast one of the selected attribute will be able to contact you. However you can always concats everybody.\nIf you do not select any attributes, all students can see you.",
      forcePasswordDescription:
        "If you select this option, the user will be forced to change their password on the first login.",
      attributeSelectionTitle: "Additional information",
      attributeSelectionDescription:
        "We ask you to anwser the questions below. The information is used to adjust our communication with you are and are not mandatory.",
      allbryCounsellor: " Allbry Counsellor",
      noAttributes:
        "No attributes found. Please contact your administrator to add attributes.",
      journalAccess: "Journal access",
      journalAccessDescription: "You can manage journal access for users here.",
      noJournalAccess: "No journal access",
      journalAccessRequired: "Select at least one journal.",
    },
    enterFieldName: "Enter Field name (E.x. Class)",
    result: "{{value}} result",
    results: "{{value}} results",
    noResults: "No results",
    day: "Day",
    month: "Month",
    year: "Year",
    statistic: {
      title: "Statistics",
      calendar: "Calendar",
      today: "Today",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      thisWeek: "This Week",
      thisMonth: "This month",
      thisQuarter: "This quarter",
      thisYear: "This year",
      allTime: "All time",
      userTypes: "User types",
      notFound: "No statistics found",
      topics: "Student reports",
      amountOfReports: "Amount of reports",
      experience: "Experience",
      counselling: "Appointments",
      popularTimes: "Popular hours",
      ngoClicks: "Support lines",
      usersAmount: "{{amount}} users",
      levelOfWorry: "Level of worry",
      chats: "Chats",
      video: "Video",
      other: "Other",
      appointments: "Appointments",
      all: "All",
      without0: "Actual worry level",
      without0tooltip:
        "Enabled: Average level of worry is shown for the meetings where a concern has actually been indicated. \nDisabled: Average level of worry for all meetings is displayed even where worry was not experienced.",
      allTitle: {
        users: "{{value}} users",
        user: "{{value}} users",
        times: "{{value}} times",
        time: "{{value}} times",
        zero: "0",
        redirection: "{{value}} redirection",
        redirections: "{{value}} redirection",
      },
      registered: "Registered",
      inactive: "Inactive",
      active: "Registered & active",
      todayInterval: "Today {{start}} - {{end}}",
      pdfExport: "Export as PDF",
      chartTitle: "Counselling",
      student: "Student statistics",
      internal: "Internal EHT statistics",
      totalHours: "Total hours of reports",
      showAll: "Show all",
      showLess: "Show less",
      anonymousUsersCount: "Amount of anonymous users - {{count}}",
      selectTopicsCategories: "Select topics categories",
      includeMeetingKind: "Include Manual reports",
      includingManualReports: "Including Manual reports",
      notIncludingManualReports: "Not including Manual reports",
      moreThanOneChatCountAverage:
        "Average number of chats for students that chatted more than once:",
      oneChatCountPercentage: "Number of students that only chatted once:",
    },
    internalStatistic: {
      time: "Time spent per group",
      total: "Total",
      hours: "hours",
    },
    changeAttributesNotes:
      '- If you want to change attributes select and show the input box to write your change, then press the "Save" button to save the changes attributes. \n - If you only select attributes and press the "Save" button, you will attribute delete. \n - If you want to add a new attribute, write your new attribute, then press the "Save" button to save the new attribute. ',
    requestOnboardingEmailSuccess:
      "Request for Onboarding Email submitted successfully",
  },
  admin: {
    title: "Allbry Admin",
    emailStatistics: {
      description:
        "Monitor email delivery health from the SMTP/Nodemailer pipeline.",
      from: "From",
      to: "To",
      invalidDate: "End date must be after start date.",
      status: {
        label: "Status",
        placeholder: "Any status",
        all: "All statuses",
        sent: "Sent",
        delivered: "Delivered",
        deferred: "Deferred",
        failed: "Failed",
        rejected: "Rejected",
        bounced: "Bounced",
      },
      template: {
        label: "Template",
        placeholder: "e.g. CHECK_IN_AVAILABLE",
      },
      limit: {
        label: 'Limit',
        placeholder: 'Select a limit',
      },
      topN: "Top {{count}}",
      range: {
        last7: "Last 7 days",
        last30: "Last 30 days",
        last90: "Last 90 days",
        last365: "Last 365 days",
        allHistory: "All history",
      },
      apply: "Apply filters",
      clear: 'Clear',
      fetchError: "Unable to load email metrics right now.",
      charts: {
        daily: {
          title: "Daily status by day",
          subtitle: "Stacked area of counts per status.",
        },
        failureRate: {
          title: "Template failure rate",
          subtitle: "Sorted by highest failure percentage.",
        },
        topTemplates: {
          title: "Top templates by volume",
          subtitle: "Highest sending templates in the selected window.",
        },
        statusByTemplate: {
          title: "Volume by status per template",
          subtitle: "Stacked bars per template by status.",
        },
        errors: {
          title: "Top error responses",
          subtitle: "Normalized SMTP errors and responses.",
        },
      },
      empty: {
        daily: "No email traffic for this window.",
        failure: "No failures recorded for this range.",
        top: "No template volume to display.",
        statusByTemplate: "No template breakdown found.",
        errors: "No errors found for this selection.",
      },
      table: {
        message: "Message",
        total: "Total",
        errors: "Errors",
        smtpResponses: "SMTP responses",
        unknownTemplate: "Unknown template",
        unknownMessage: "Unknown message",
        unknown: "Unknown",
      },
    },
    modals: {
      addCounsellors: {
        choose: "Choose student health staff",
        search: "Search student health staff",
      },
    },
    organization: {
      viewPage: {
        principalsDescription: "People responsible for this organization.",
        staffDescription: "Student health staff assigned to this organization.",
        noPrincipals: "No principals have been added to this organization.",
        loadError: "Unable to load organization",
        loadErrorHint: "Try again to view the organization details.",
        retry: "Try again",
      },
      editor: {
        editDescription: "Manage organization details, people and access in one place.",
        createDescription: "Set up the organization and the people who will manage it.",
        overview: "Overview",
        staff: "Student health staff",
        connections: "Access & connections",
        newOrganization: "New organization",
        organizationAccount: "Organization account",
        navigation: "Organization sections",
        onThisPage: "On this page",
        separateSections: "Access, subscriptions and roles are saved in their own sections.",
        generalDescription: "Identity, active period and account hierarchy.",
        principalsDescription: "Manage the people responsible for this organization.",
        staffDescription: "Choose the Allbry staff assigned to this organization.",
        connectionsDescription: "Manage sign-in and the linked healthcare unit.",
        subscriptionsDescription: "Choose which services are available to this organization.",
        rolesDescription: "Manage roles and the permissions they grant.",
        savedSeparately: "Saved separately",
        imageHint: "Add an image to help identify this organization.",
        imageLimit: "Image files up to 7 MB.",
        parentAccount: "Parent account",
        parentPlaceholder: "No parent account",
        namePlaceholder: "Enter organization name",
        parentHint: "Choose a parent account by name, or leave empty for no parent account.",
        parentSearch: "Search accounts by name",
        parentNoResults: "No matching accounts found",
        parentUnavailable: "Unavailable parent account",
        invalidParentAccount: "Choose an available account that is not this organization or one of its child accounts.",
        active: "Registered",
        deactivated: "Deactivated",
        scheduled: "Scheduled",
        newPrincipal: "New principal",
        removePrincipal: "Remove {{name}}",
        removeStaff: "Remove {{name}}",
        assigned: "Assigned",
        emptyStaffTitle: "No student health staff assigned",
        emptyStaffDescription: "Add staff who should work with this organization.",
        unsavedChanges: "Unsaved changes",
        noChanges: "No unsaved changes",
        reviewErrors: "Review the highlighted fields before saving.",
        saveScope: "Saves organization details, principals and assigned staff.",
      },
      organizations: "organizations",
      edit: "Edit organization",
      details: "Organization details",
      general: "General",
      closed: "Closed",
      start: "Start Date",
      end: "End Date",
      principal: "Principal",
      principals: "Principals",
      create: "Create organization",
      view: "View",
      editPopup: "Edit",
      deactivated: "Organization deactivated",
      deactivatedError: "Error deactivating organization",
      name: "Organization Name",
      startEnd: "Start date/End date",
      addPrincipal: "+ Add principal",
      addCounsellor: "+ Add student health staff",
      searchOrganizations: "Search organizations by name",
      edited: "The organization was edited successfully.",
      created: "The organization Was created successfully.",
      createdError: "Error creating organization",
      editError: "Error editing organization",
      longName: "Too long name",
      startDateRequired: "Start date is required",
      endBeforeStart: "End date cannot be before start date",
      longEmail: "Too long email",
      emailFormat: "Wrong email format",
      duplicateEmails: "Duplicate emails",
      userExists: "User already exists",
      atLeastOne: "At least one must be active",
      subscriptionType: {
        title: "Subscription types",
        description:
          "Add a service to make it available to this organization. Some services also require a registration code.",
        addLink: "+ Add feature or type",
        selectType: "Select type",
        registrationCode: "Registration code",
        add: "Add",
        remove: "Remove",
        addDialogTitle: "Add subscription type",
        addSuccess: "Subscription type has been added successfully",
        removeSuccess: "Subscription type has been removed successfully",
      },
      deleteOrganization: "Delete organization",
      requestScrapeSuccess:
        "Request for Scrape Multiple Organizations submitted successfully",
      scrapeMultipleOrganizationDialogInstruction:
        "You will scrape the following organizations",
      cannotBeOwnParent: "Organization cannot be its own parent",
    },
    role: {
      title: "Roles",
      addRoles: "+ Add role",
      createRole: "Create role",
      editRole: "Edit role",
      chooseMasterRole: "Choose master role",
      enterNameRoleSwedish: "Enter name of role swedish",
      enterNameRoleEnglish: "Enter name of role english",
      chooseFeatures: "Choose features",
      selectMasterRole: "Select base role",
      selectBaseRoleFirst: "Select a base role first to choose a role name.",
      customRolesCreated: "Custom role created successfully",
      errorCreateCustomRoles: "Error while create custom role",
      updatedCustomRoles: "Updated custom role successfully",
      errorUpdatingCustomRoles: "Error updating custom role",
      removeCustomRoles: "Custom role removed successfully",
      errorRemovingCustomRoles: "Error while removing custom role",
      confirmRemoveCustomRoles:
        "Are you sure you want to remove this role? This action cannot be undone.",
      rolesRequired: "Roles required",
      rolesSelectRequired: "Select at least one role",
      chooseFeaturesOne: "Choose at least one feature",
      sessionTimeRequired: "Session time required",
      sessionTimeNumber: "Session time should be number",
      sessionTimeMin: "Session time should be greater than 1 hour",
      sessionTimeTitle: "Session time",
      sessionTimeDescription:
        "How long a student's session stays active before they need to log in again.",
      sessionTime1Day: "1 Day",
      sessionTime7Days: "7 Days",
      sessionTime30Days: "30 Days",
      sessionTime1DayDesc: "Short-term access",
      sessionTime7DaysDesc: "Weekly access",
      sessionTime30DaysDesc: "Monthly access",
      createRoleSubtitle:
        "Set up a new role with a name, features and session duration",
      editRoleSubtitle: "Update role details, features and session duration",
      baseRoleSection: "Base role",
      roleNameSection: "Role name",
      noBaseRole: "No base role",
      featuresLabel: "Features",
      noFeaturesSelected: "No features selected",
      featuresSelectedCount: "{{count}} of {{total}} selected",
      featuresSelectedAll: "All {{count}} selected",
      selectAllFeatures: "Select all",
      clearAllFeatures: "Clear all",
      customRoleNameLabel: "+ Custom",
      customRoleNameDesc: "Enter your own role name",
      roleNamePlaceholder: "Role name...",
      readyToSave: "Ready to save",
      sessionDurationTitle: "Login session duration",
      sessionDurationHelper:
        "How long a user stays active before they need to log in again.",
      allFeaturesWarning:
        "All permissions are selected. Review access before saving.",
      roleSummaryBase: "Base",
      roleSummaryRole: "Role",
      roleSummaryFeatures: "Features",
      roleSummarySession: "Session",
    },
    counsellors: {
      title: "Allbry Counsellors",
      add: "Add counsellor",
      edit: "Edit counsellor",
      profile: "Counsellor's profile",
      searchCounsellors: "Search counsellors by name",
    },
    ftpDataCollection: {
      title: "FTP Data Collection",
      selectOrganizations: "Select Organization",
      runCron: "Run Now",
      cronRunStart:
        "Data migration process started for the selected organization. You will be received an email shortly",
    },
    configData: {
      title: "Config Data",
      addConfig: "Add Config",
      organization: "Organization",
      migrationTime: "Migration Time",
      status: "Status",
      active: "Active",
      deactivate: "Deactivated",
      viewProfile: "View Config",
      editProfile: "Edit Config",
      organizationActivated: "Organization Activated",
      organizationDeactivated: "Organization Deactivated",
      activatingError: "Error activating Organization",
      deactivatingError: "Error deactivating Organization",
      configDetails: "{{organization}}'s Config Details",
      timerNotSet: "Timer Not Set",
      yes: "Yes",
      no: "No",
    },
    configForm: {
      title: "Config Form",
      addOrgConfig: "Add organization Config",
      editOrgConfig: "Edit organization Config",
      ftpConfigDetails: "FTP Config Details",
      organization: "Organization",
      runOnce: "Run Single Time",
      timer: "Timer",
      daily: "Every Midnight",
      weekly: "Every Sunday Midnight",
      monthly: "Every 1st Of Month Midnight",
      selectTimer: "Select Timer",
      subOrganization: "Sub Organization",
      addOrganization: "+ Add Organization",
      selectOrganizations: "Select Organization",
      userModel: "User Models",
      addModel: "+ Add Model",
      student: "Student",
      attribute: "Attribute",
      userAttributes: "User Attribute",
      addAttribute: "+ Add Attribute",
      selectAttributes: "Select Attribute",
      name: "Name",
      code: "Code",
      host: "Host Name",
      username: "Username",
      password: "Password",
      port: "Port",
      secure: "Secure",
      fileName: "File Name",
      fileFormat: "File Format",
      filePath: "File Path",
      true: "True",
      false: "False",
      fileNameEx: 'Ex: File name is "latest" or create \nyour custom file name',
      filePathEx: 'Ex: File path is "/"  ',
      userModelNameEx: 'Ex: name is "student/counsellor"',
      userModelCodeEx: 'Ex: code is "student/counsellor"',
      edit: "Edit Config",
      nameRequired: "Name is required",
      codeRequired: "Code is required",
      selectRequired: "Select any one",
      configRequired: "{{name}} is required",
    },
    notifications: {
      notificationDate: "Notification Date",
      message: "Message",
      activeInactive: "Active/Inactive",
      status: "Status",
      all: "All",
      sent: "Sent",
      upcoming: "Upcoming",
      vieweditNotifications: "View/edit notifications",
      deleteNotifications: "Delete notifications",
      createNotification: "Create notification",
      notificationDateAndTime: "Notifications date and time",
      notificationMessage: "Notification Message",
      notificationMessagePlaceholder: "Enter notification message",
      allOrganization: "All Organization",
      selectOrganization: "Select Organization",
      add: "Add",
      popup: "Are you sure to remove notification?",
      messageValidation: "Message can not be blank",
      selectRequired: "Select at least one organization",
      createdNotification: "Notification create successfully",
      removedNotification: "Notification removed successfully",
      editNotification: "Notification edit successfully",
      errorCreateNotification: "Error while creating notification",
      errorErrorNotification: "Error while edit notification",
    },
    superActions: {
      title: "Super Actions",
      userLoginWithEmail: "User login with email",
      loginButton: "Login",
      emailInput: "Email",
    },
    broadcastMessages: {
      createdBroadcastMessage: "Broadcast message create successfully",
      updatedBroadcastMessage: "Broadcast message updated successfully",
      removedBroadcastMessage: "Broadcast message removed successfully",
      deleteBroadcastMessageConfirmation:
        "Are you sure you want to delete this record ?",
      deleteBroadcastMessageError:
        "You cannot delete this record but can deactivate it",
      editBroadcastMessage: "Broadcast message edit successfully",
      errorCreateBroadcastMessage: "Error while creating broadcast message",
      errorUpdateBroadcastMessage: "Error while updating broadcast message",
      errorRemoveBroadcastMessage: "Error while removing broadcast message",
      allOrganizations: "All Organizations",
      selectOrganization: "Select Organization",
      active: "Active",
      inActive: "Inactive",
      edit: "Edit",
      delete: "Delete",
      create: {
        startDate: "Start Date",
        endDate: "End Date",
        enterHeading: "Enter Heading",
        message: "Enter Message",
        newsAndMarketingMessage: "News and Marketing Message",
        add: "Add",
        heading: "Heading",
        userTypes: "User Types",
        selectUserTypes: "Select User Type",
      },
      userTypes: {
        counsellor: "Counsellor",
        student: "Student",
      },
      listingColumn: {
        heading: "Heading",
        message: "Message",
        startDate: "Start Date",
        endDate: "End Date",
        userType: "User Type",
        status: "Status",
      },
    },
    healthcare: {
      validation: {
        maxLength: "Use no more than {{max}} characters",
      },
      pageTitle: "Healthcare structure",
      pageSubtitle: "Configure healthcare providers and units independently of organizations",
      addProvider: "+ New healthcare provider",
      addUnit: "+ New healthcare unit",
      formSections: {
        providerDetails: "Provider details",
        unitDetails: "Unit details",
        contactInformation: "Contact information",
        address: "Address",
        identification: "Identification",
        status: "Status",
      },
      feedback: {
        retry: "Try again",
        clearSearch: "Clear search",
        loadingProviders: "Loading healthcare providers…",
        loadingUnits: "Loading healthcare units…",
        noSelection: "No provider selected",
        providersHint: "Choose a provider to view its details, or create a new healthcare provider to get started.",
        searchHint: "Try a different search or clear it to see available providers.",
        retryHint: "Try loading the provider list again to view provider details.",
        unitsRetryHint: "We couldn't load this provider's units. Try again to view and manage them.",
        unitsHint: "Add the first healthcare unit for this provider. You can then link organizations to it from the organization module.",
        countsError: "Unit counts are unavailable.",
        manageUnit: "Manage unit",
        editUnit: "Edit {{name}}",
      },
      providerList: {
        title: "Healthcare providers",
        searchPlaceholder: "Search healthcare providers",
        showInactive: "Show inactive",
        hideInactive: "Hide inactive",
        unitsCount_one: "{{count}} unit",
        unitsCount_other: "{{count}} units",
        empty: "No healthcare providers found",
        fetchError: "Unable to load healthcare providers",
      },
      providerDetail: {
        editProvider: "Edit healthcare provider",
        organizationNumber: "Organization number",
        orgNrShort: "Org. no.",
        hsaId: "HSA ID",
        noneSelected: "Select a healthcare provider from the list on the left",
      },
      unitsTable: {
        title: "Healthcare units",
        description: "Organizations are linked to one of these units from the organization module.",
        addUnit: "+ Add healthcare unit",
        noEmail: "No functional email",
        columns: {
          unit: "Healthcare unit",
          hsaId: "HSA ID",
          schoolUnitCode: "School unit code",
          linkedOrganizations: "Linked organizations",
          status: "Status",
        },
        linked_one: "{{count}} linked",
        linked_other: "{{count}} linked",
        none: "None",
        empty: "No healthcare units yet",
        fetchError: "Unable to load healthcare units",
      },
      unitEdit: {
        name: "Unit name",
        email: "Functional email",
        hsaId: "HSA ID",
        schoolUnitCode: "School unit code",
        save: "Save changes",
        saved: "The healthcare unit was updated successfully.",
        saveError: "Error updating the healthcare unit",
        requiredName: "Unit name is required",
      },
      providerForm: {
        createTitle: "Create healthcare provider",
        editTitle: "Edit healthcare provider",
        name: "Name",
        organizationNumber: "Organization number",
        email: "Functional email",
        phone: "Phone",
        address: "Address",
        postalCode: "Postal code",
        city: "City",
        hsaId: "HSA ID",
        active: "Active",
        cancel: "Cancel",
        create: "Create healthcare provider",
        save: "Save changes",
        createdSuccess: "The healthcare provider was created successfully.",
        updatedSuccess: "The healthcare provider was updated successfully.",
        createError: "Error creating the healthcare provider",
        updateError: "Error updating the healthcare provider",
        requiredName: "Name is required",
      },
      unitForm: {
        title: "The healthcare unit will be created under {{providerName}}",
        name: "Unit name",
        email: "Functional email",
        hsaId: "HSA ID",
        schoolUnitCode: "School unit code",
        cancel: "Cancel",
        create: "Create healthcare unit",
        createdSuccess: "The healthcare unit was created successfully.",
        createError: "Error creating the healthcare unit",
        requiredName: "Unit name is required",
      },
      sso: {
        title: "SSO",
        idp: "Identity provider",
        statusEnabled: "Enabled",
        statusDisabled: "Disabled",
        edit: "Edit",
        setup: "Set up SSO",
        notConfigured: "SSO has not been set up for this organization yet.",
        inheritedNote: "Inherited from the parent organization",
        modalTitle: "SSO configuration",
        idpLabel: "IdP",
        idpPlaceholder: "e.g. microsoft, google...",
        quickSelect: {
          microsoft: "Microsoft",
          google: "Google",
          mobilityguard: "Mobility Guard",
          skolfederation: "Skolfederation",
        },
        metadata: "Metadata (XML)",
        entryPoint: "Entry point (URL)",
        issuer: "Issuer",
        cert: "Certificate (PEM)",
        enableSso: "Enable SSO",
        close: "Close",
        save: "Save SSO configuration",
        saveSuccess: "The SSO configuration was saved successfully.",
        saveError: "Error saving the SSO configuration",
        fetchError: "Unable to load the SSO configuration",
        entryPointInvalid: "Enter a valid URL",
        issuerRequired: "Issuer is required",
      },
      emiConfig: {
        title: "Healthcare unit",
        selectLabel: "Link a healthcare unit",
        selectPlaceholder: "Select a healthcare unit",
        save: "Save link",
        unlink: "Remove link",
        saveSuccess: "The link was saved successfully.",
        saveError: "Error saving the link",
        fetchError: "Unable to load the healthcare unit link",
        empty: {
          title: "No healthcare unit linked",
          subtitlePrefix: "Select a unit above or create it first in ",
          linkLabel: "Healthcare structure",
        },
        preview: {
          provider: "Healthcare provider",
          hsaId: "HSA ID",
          schoolUnitCode: "School unit code",
          email: "Functional email",
        },
      },
      status: {
        active: "Active",
        inactive: "Inactive",
      },
    },
  },
  allbryCounsellor: {
    choose: "Choose organization",
    notFound: "No organization found...",
    text: "You haven't been added to any organization yet.",
    error: "Error while attempting to login in organization",
  },
  filterModal: {
    filter: "Filter",
    addFilter: "Add filter",
    description: "Show users that match all conditions:",
    addCondition: "+ Add condition",
    is: "is",
    isNot: "is not",
    chooseFilter: "Choose filter",
    conditionValue: "Enter condition value",
  },
  counsellorReport: {
    tabs: {
      reportTemplates: "Report templates",
      reports: "Reports",
      customReports: "Custom reports",
    },
    reports: "Reports",
    reportTemplate: {
      studentReport: "Student report",
      studentReportDescription: "Create a student report",
      studentNote: "Student note",
      studentNoteDescription: "Create a student note",
      internalReport: "Internal EHT report",
      internalReportDescription: "Create a internal EHT report",
      anonymousReport: "Anonymous student report",
      anonymousReportDescription: "Create an anonymous student report",
    },
    internalReport: {
      selectTypes: "2. Select the type of meeting",
      selectFilter: "3. Select group if applicable (non mandatory)",
      meetingDuration: "5. Meeting duration (in hours)",
      meetingNotes: "4. Meeting notes",
      meetingNoteTitle: "Anteckningar",
      meetingTypes: {
        type1: "EHT",
        type2: "EHM",
        type3: "Cooperation meeting SIP",
        type4: "Cooperation meeting with VH, students & other actors",
        type5: "Cooperation meeting BUP",
        type6: "Meeting with VH, students & other actors",
        type7: "Meeting with student and VH",
        type8: "Meeting with VH",
        type9: "Meeting with student and school staff",
        type10: "Meeting with VH and another actor",
        type11: "Meeting cooperation",
        type12: "Attendance work",
        type13: "Guiding school staff",
        type14: "Meeting with school staff",
        type15: "Meeting with principal",
        type16: "Participation in development work",
        type17: "Participation in security work",
        type18: "Work with student groups",
      },
      staticAttributes: {
        attributeName: {
          attribute1: "Stage",
        },
        attributeValue: {
          attribute1value1: "Low stage",
          attribute1value2: "Middle stage",
          attribute1value3: "High stage",
          attribute1value4: "High school",
        },
      },
    },
    anonymousReport: {
      title: "Anonymous student report",
      selectAttributeTitle: "1. Select class/year (non mandatory)",
      noAttributes:
        "No attributes found. Please contact your administrator to add attributes.",
      gender: "2. Gender identity (non mandatory)",
      studentName: "3. Enter students name (non mandatory)",
    },
    chooseStudent: "Choose student",
    chooseCustomStudent: "Choose {{label}}",
    noResultsFound: "No {{label}} found",
    createReport: "Create report",
    createNote: "Create note",
    searchStudents: "Search students",
    filterOption: {
      notes: "Notes",
      quickReport: "Student reports",
      internalReport: "Internal reports",
      anonymousReport: "Anonymous reports",
      customReport: "Custom reports",
      selectReport: "Select report type",
    },
    attributeFilter: {
      filter: "Filter",
      title: "Filter by attributes",
      search: "Search attributes",
      noResults: "No matching attributes found",
      selectAll: "Select all",
      selectAllValues: "Select all values",
      clearAll: "Clear all",
    },
    showMore: "Show more...",
    uploadDocument: "Upload document",
    noReports: "No reports are available",
    sortNewestFirst: "Newest first",
    sortOldestFirst: "Oldest first",
  },
  survey: {
    questionTypes: {
      comment: "Multi-line answer",
    },
    introStep: "Hey, let's start with a quick survey",
    deleteSuccess: "Survey deleted successfully",
    firstStep: {
      question: "Have you tried Allbry before?",
      letMeIn: "Yes, let me in!",
      whatIsAllbry: "What is Allbry?",
    },
    secondStep: {
      question: "Have you previously sought guidance?",
      yes: "Yes, a couple of times",
      never: "No, I haven't",
    },
    genderStep: {
      question: "To which gender do you most identify?",
      male: "Male",
      female: "Female",
      other: "Other",
      private: "Do not wish to say",
    },
    outroStep: "Thank you for your time!",
    helpImprove:
      "It'll take only few minutes, but will help us to improve your experience with Allbry.",
    firstPart: "1/2",
    secondPart: "2/2",
    page: "Page",
    checkInAndSurvey: "Check-ins and Surveys",
    tabOption: {
      template: "Templates",
      active: "Active",
      statistics: "Statistics",
    },
    create: {
      title: "Template creator",
      name: "Name",
      accessType: "Access type",
      accessTypeOptions: {
        global: "Global",
        local: "Local",
      },
      surveyType: "Survey type",
      surveyTypeOptions: {
        checkIn: "Check-in",
        survey: "Survey",
      },
      isPublic: "Is Public",
      isActive: "Is Active",
    },
    createConfig: {
      title: "Config Templates",
      Survey: "Survey Template",
      Account: "Organization",
      attributes: "User Attributes",
      select: "-Select-",
      runsDay: "Runs every Day",
      runsWeekly: "Runs every",
      runsMonthly: "Runs every {{date}} th of month",
      timerOption: {
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        weekDays: {
          monday: "Monday",
          tuesday: "Tuesday",
          wednesday: "Wednesday",
          thursday: "Thursday",
          friday: "Friday",
          saturday: "Saturday",
          sunday: "Sunday",
        },
      },
      modals: {
        students: {
          title: "Select Group",
          chooseStudentTitle: "Who do you want to check on?",
          selectedStudents: "Selected Students",
          confirmSelection: "Confirm Selection",
          searchPlaceholder: "Search for students",
          noStudentsFound: "No students found",
        },
      },
      addOwnUsers: "+ Add your own users",
      addedOwnUsers: "Added {{count}} own users",
    },
    template: {
      createTemplate: "Template create successfully",
      errorCreateTemplate: "Error while creating template",
      deleteTemplate: "Template delete successfully",
      deleteTemplateConfirmation:
        "Are you sure you want to delete this templates ?",

      deleteTemplateError:
        "You cannot delete this template but can deactivate it",
      deleteConfigError: "You cannot delete this config but can deactivate it",
      errorDeleteTemplate: "Error while deleting template",
      editTemplate: "Template edit successfully",
      errorEditTemplate: "Error while editing template ",
      createConfig: "Config create successfully",
      errorCreateConfig: "Error while creating config",
      active: "Active",
      inActive: "Inactive",
      true: "Yes",
      false: "No",
      timer: "Timer",
      RunOnce: "RunOnce",
      StartTime: "Start time",
      EndTime: "End time",
      TimeLimitInHour: "Time limit in Hour",
      typeChangeDescription:
        "We are looking you have some changes on survey Templates.\n Are you sure you want to change?\n If you change the survey type then you all changes will be discard.",
      btn: {
        delete: "Delete",
        edit: "Edit",
        statistics: "Statistics",
        addImage: "Add image to question",
      },
      listingColumn: {
        name: "Name",
        templateName: "Template Name",
        accessType: "Access type",
        surveyType: "Survey type",
        public: "Public",
        questionAmount: "Question amount",
        status: "Status",
        attributes: "User Attributes",
        organization: "Organization Name",
        isAnonymous: "Is Anonymous",
        action: "Action",
        StartDate: "Start Date",
        EndDate: "End Date",
      },
      questionType: {
        filling: "Feelings",
        rangSlider: "Range slider",
        singleSelect: "Single select",
        image: "Image",
      },
      createTemplateAboutDescription:
        "Watch the video below to learn more about the template creator.",
      templateVideoLink: "Link to the video",
      createTemplateAboutTitle: "Description",
      duplicate: "Duplicate",
      copyText: "Copy",
      addImages: "Add images",
    },
    student: {
      question: "Question",
      stripMas: "Your teacher wants to take the test!",
      questionnaire: "The school questionnaire",
      completeHeading: "Thanks for your answers!",
      completeDescription: "The school questionnaire has been submitted.",
      completeBackButton: "Back to home screen",
      startSurvey: "Start the survey",
      skipQuestion: "I do not want to answer",
      SkipMas: "Are you sure, you don't want to answer?",
      notAnonymousAnswerDescription:
        "The survey is not anonymous, and the student health services can access individual responses",
    },
    statistics: {
      noAnyTemplate: "You have no any templates",
      all: "All",
      ended: "Ended",
      started: "Started",
      date: "Date",
      lastFillUpDate: "Last fill up date",
      no: "No.",
      name: "Name",
      individualResponse: "Individual response",
      AmountOfAnswers: "Amount of answers",
      noOFUsers: "No. of users",
      question: "question",
      thisMonth: "This month",
      unanswered: "Unanswered",
      status: {
        new: "New",
        complete: "Complete",
        active: "Active",
      },
      showAll: "Show all",
      showLess: "Show less",
      response: "response",
      days: "Days",
      weekNumbers: "Week numbers",
      months: "Months",
      students: "Students",
      noAnyoneAnswered: "No students have answered this survey",
      answer: "Answer",
      deepFilter: "Deep filter",
      averageValue: "Average value: {{value}}",
      multiple: "(Multiple)",
      guestUserLink: "Guest user link",
      showGuestUserData: "Show guest user data",
      filterInfo: "If a filter is applied, remove the guest user data.",
      guestUsers: "guest users",
      showGuestUserDataDisabledContent:
        "Not possible to show guest user data if a filter is applied.",
      amountOfCustomStudents: "Manually chosen students",
      numberOfCustomStudents: "Number of manually chosen students",
      anonymousResponse: "Response #{{number}}",
      responses: "Responses",
      otherAnswers: "Other answers",
      page: "Page",
      searchStudents: "Search students",
      noSearchResults: "No students match your search",
    },
    counsellor: {
      titles: { survey: "Survey", checkIn: "Check in" },
      readMoreDifferenceModalTitle: "Difference",
      checkInDescription:
        "A recurring survey with questions asked at regular intervals according to a selected time interval. It is used to continuously collect data, identify trends and track changes over time. Perfect for measuring student well-being or other factors that require regular follow-up. By analyzing the results over time, insights can be created that contribute to better decision-making and long-term development.",
      surveyDescription:
        "A one-time survey with questions asked at a specific time. It is used to quickly gather information, evaluate an event, or get a snapshot of student opinions and experiences. It is well suited for safety and health surveys, school surveys, or evaluations where a single data collection is sufficient. The results can provide valuable insights for making decisions or improving future processes.",
      title: "The Temperature check",
      startTitle: "Pace your students",
      startDescription:
        "With the temperature check, you can send out long or short questionnaires to learn more about the well-being of your students.",
      createTemplateBtn: "Create your first temp",
      addTemplateBtn: "Create new temp",
      readMoreDifference: "Read more about the difference",
      student: "Students",
      ownSelection: "Own selection",
      ownSelectionDescription: "Select individuals",
      answered: "Answered",
      dispatch: "Dispatch frequency",
      adjustTime: "Adjust date and time",
      question: {
        num1: "Do you want to take the test or create a survey?",
        template: "What do you want to tempt?",
        attribute: "Which ones do you want to challenge?",
        selectedAttribute: "selected atttribute",
        survey: "How often do you want to take the temp?",
        checkIn: "When do you want to send out the survey?",
        description: {
          checkIn:
            "Survey with uestions that are asked regularly during a selected interval",
          survey: "Survey with questions asked once",
        },
        anonymousPageTitle: "Do you want to see individual answers?",
        anonymousPageDescription:
          "If you activate this, you will be able to see students individual answers, however the student will be informed when starting the survey that their answers will not be anonymous",
        guestPageTitle: "Do you want to generate a anonymous link?",
        guestPageDescription:
          "If you activate this, you will receive a link and/or QR-code that you can distribute to students. \n The students who then use the link will be able to answser the survey without logging in and/or having an account.",
        info: "Info",
        infoDescription:
          "This survey currently includes a file upload question. As guest users are not permitted to upload files, please remove the file upload question before sharing the survey with guest users.",
      },
      status: {
        active: "Active",
        search: "Search",
        completed: "Completed",
      },
      timerOption: {
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        dailyDescription: "Shipped every day",
        weeklyDescription: "Shipped every Monday",
        monthlyDescription: "Sent on the 1st of every month",
      },
      createOwnTemplate: "+ Create your own template",
    },
    templateType: {
      general: "General",
      private: "Private",
    },
    surveyTypeOptions: {
      checkIn: "Check-in",
      survey: "Survey",
    },
    amountQuestion: "Amount of questions",
    numberOfQuestion: "Number of questions",
    endSurveyConfig: "End survey",
    endSurveyPopupConfirmationText:
      "Are you sure you want to stop this survey?",
    endSuccess: "Survey ended successfully",
    deleteSurveyConfig: "Delete survey",
    deleteSurveyConfigPopupConfirmationText:
      "Are you sure you want to delete this survey and its statistics?",
    editorLabels: {
      designer: "Designer",
      preview: "Preview",
    },
    creator: {
      title: "Survey title",
    },
    maxFileSizeError: "The maximum file size is {{size}} MB",
  },
  customReports: {
    title: "Custom Reports",
    description:
      "Here you can choose to activate and/or create your own report templates if you find that something is missing from our existing ones. Just like with the student report, graphs and statistics are generated based on the responses provided by the staff. You can also choose to create an anonymous report where none of the students' responses can be traced back to them.",
    createReportBtn: "Create a custom report",
    reminderScreen: {
      skip: "Skip",
      continue: "Continue",
      title: "Title",
      titlePlaceholder: "Please enter title",
      titleTooLong: "Title must be {{max}} characters or fewer.",
      question: "Do you want to set a reminder?",
      selectDueDate: "Select due date",
      description:
        "Colleagues tagged in the report will also get a reminder. Go to the activities tab on the homepage to see your reminders.",
    },
    journalAccessScreen: {
      description: "Where do you want the report to be created?",
      chooseJournal: "Choose journal",
    },
    template: {
      title: "Template creator",
      type: {
        general: "General",
        private: "Private",
      },
      duplicate: "Duplicate",
      copyText: "Copy",
      name: "Name",
      namePlaceholder: "Please enter name",
      description: "Description",
      descriptionPlaceholder: "Please enter description",
      accessTypeLabel: "Access type",
      accessTypePlaceholder: "Please select access type",
      accessType: {
        global: "Global",
        local: "Local",
      },
      public: "Public",
      active: "Active",
      createTemplateAboutTitle: "Description",
      createTemplateAboutDescription:
        "Watch the video below to learn more about the template creator.",
      templateVideoLink: "Link to the video",
      createTemplateSuccess: "Template create successfully",
      errorCreateTemplate: "Error while creating template",
      updateTemplateSuccess: "Template update successfully",
      errorUpdateTemplate: "Error while updating template",
      deleteTemplateConfirmation:
        "Are you sure you want to delete this template?",
      createTemplateSecretLevelConfirmationLow:
        "Are you sure you want to activate the report with a low confidentiality level?",
      createTemplateSecretLevelConfirmationHigh:
        "Are you sure you want to activate the report with a high confidentiality level?",
      createTemplateSecretLevelDescription:
        "The following roles will have access to this report type: {{description}}",
      deleteTemplateSuccess: "Template delete successfully",
      errorDeleteTemplate: "Error while deleting template",
      confirmDialog: {
        editTemplate: {
          header: "Are you sure you want to edit this template?",
          body: "If you edit the template, the changes will apply to every report type that uses it, including reports that have already been created.",
        },
      },
    },
    information: "Information",
    taggedInformation:
      "You can choose to tag a colleague in the report. This will send a notification to the tagged colleague.",
    statistics: {
      selectTemplate: "Select template",
      users: "Users",
      allReports: "All reports",
      normalReport: "Normal report #{{number}}",
      anonymousReport: "Anonymous report #{{number}}",
      resultAdditionalInfo: {
        dateAndTime: "Date and time",
        user: "User",
        reporter: "Reporter",
        counsellor: "Counsellor",
        goToReport: "Go to report",
      },
    },
    config: {
      screen1: {
        title: "Choose template or create your own",
        createTemplate: "Create your own template",
      },
      screen2: {
        title: "Roles with access and confidentiality level",
        person: "{{count}} person",
        persons: "{{count}} persons",
      },
      screen3: {
        title: "Do you want to allow anonymous reports?",
        allowAnonymousReportsDescription:
          "If you activate this, users will be able to create anonymous reports. This means that staff will not need to select a student when using the report.",
      },
      screen4: {
        title: "Choose confidentiality level",
        allowPriorityDescription:
          "You need to select a confidentiality level for the report type you have created. The selected level determines how the reports can be shared within the staff group. <br><br> A high confidentiality level restricts access to individual student responses and only provides anonymized statistics to staff roles not explicitly granted access. Free-text responses are entirely hidden, as they may contain identifiable information. This level is suitable when sensitive information should only be handled by specific professional roles. <br><br> A low confidentiality level means that the entire staff group can access complete reports, including individual responses and the names of the students concerned. Free-text responses are also visible, which increases transparency but requires careful handling of personal data.",

        highPriorityDescription:
          "A high confidentiality level means that individual reports are not visible to all staff members. Only the professional roles selected in the previous step will have access to individual responses. Other staff members will only see aggregated statistics, with no link to specific students. Additionally, no statistics will be shown for free-text questions, as these may include identifying information.",

        lowPriorityDescription:
          "A low confidentiality level means that all staff members have access to individual responses in the reports, including which students the reports refer to. This means there are no restrictions on which professional roles can view the content. Free-text responses are also visible, which may include personally identifiable information. This level may be suitable in situations where the entire team needs full insight in order to provide appropriate support.",
        high: "High",
        low: "Low",
      },
      screen5: {
        title: "Do you want to allow reminder for this reports?",
        allowReminderDescription:
          "If you activate this, users will be able to set reminders for this report. This means that staff will receive notifications according to the reminder settings.",
      },
      createSuccess: "Config create successfully",
      errorCreateConfig: "Error while creating config",
      updateSuccess: "Config update successfully",
      errorUpdateConfig: "Error while updating config",
      list: {
        all: "All",
        reports: "{{count}} Reports",
      },
      amountOfQuestions: "Amount of questions",
      amountOfReports: "Amount of reports",
      secretLevel: "Secret level",
      secretLevelValues: {
        HIGH: "High",
        LOW: "Low",
      },
      areYouSure: "Are you sure want to edit secret level?",
      roles: "Roles",
      editRoles: "Edit",
      editTemplate: "Edit template",
      rolesUpdateSuccess: "CustomReport update successfully",
      errorRolesUpdate: "Error while updating customReport",
      warningDialogForNotEligibleToEditTemplate: {
        header: "You are not eligible to edit this template",
        body: "You can not edit this template because you are not eligible to edit this template. Please contact your administrator.",
      },
      restrictionDialogForNotEligibleToEditTemplate: {
        header: "Restricted access",
        body: "This is a general template and cannot be edited. If you need to make changes, please create a duplicate and edit that instead.",
      },
    },
    downloadPDF: "Download PDF",
    pdf: {
      report: "Report",
      question: "Question",
      dateAndTime: "Date and time",
      user: "User",
      reporter: "Reporter",
      counsellors: "Counsellors",
      noAnswer: "No answer",
      openInNewTabHint: "Tip: Right-click the link and open it in a new tab.",
      page: "Page",
      signedBy: "Signed by",
    },
    reasonForChange: "Reason for change",
    reasonForChangePlaceholder: "Enter the reason for change",
    changelog: "Changelog",
    changeItem: "Change #{{number}}",
    changeBy: "{{dateAndTime}} by {{user}}",
    signatureStatus: {
      draft: "Draft",
      signed: "Signed",
    },
    signReport: {
      title: "Sign report",
      description:
        "By signing you lock the report. No changes can be made without unlocking it first.",
      confirmLabel: "Confirm with your name",
      confirm: "Sign",
      cancel: "Cancel",
      nameMismatch: "The name does not match your username",
    },
    unlockReport: {
      title: "Unlock report",
      description:
        "The report will be unlocked for editing. The event is logged in the event log.",
      signedBy: "Signed by",
      reasonLabel: "Reason for unlocking",
      reasonPlaceholder: "Enter reason",
      confirm: "Unlock",
      cancel: "Cancel",
    },
    viewOtherReport: {
      title: "View custom report",
      description:
        "This report was created by another user. Do you want to continue viewing it?",
      cancel: "Cancel",
      continue: "Continue",
    },
    eventLog: "Event log",
    eventLog_report_viewed: "Report viewed",
    eventLog_report_exported: "Report exported",
    eventLog_report_signed: "Report signed",
    eventLog_report_unlocked: "Report unlocked",
    eventLog_report_updated: "Report updated",
    eventLog_report_updated_draft: "Report updated (draft)",
    eventLog_report_removed: "Report removed",
    signReport_btn: "Sign report",
    unlockReport_btn: "Unlock",
    cannotDelete: "Report cannot be deleted after it has been signed",
    allowGuestEditing: "Allow guest editing",
    allowGuestEditingEnableQuestion:
      "Are you sure you want to allow other counsellors to edit this report?",
    allowGuestEditingDisableQuestion:
      "Are you sure you want to disable guest editing for this report?",
    allowGuestEditingYesEnable: "Yes, allow",
    allowGuestEditingYesDisable: "Yes, disable",
    allowGuestEditingEnabled: "Guest editing has been enabled",
    allowGuestEditingDisabled: "Guest editing has been disabled",
  },
  contact: {
    title: "Contact Cards",
    addNew: "Add contact",
    edit: "Edit contact",
    editSubtitle: "Update contact information and relationship settings.",
    firstName: "First name",
    lastName: "Last name",
    personalNumber: "Personal identity number",
    address: "Address",
    phoneMobile: "Phone",
    phoneWork: "Work phone",
    email: "Email",
    notes: "Notes",
    notesPlaceholder: "Add additional information about this contact...",
    relation: "Relation to student",
    legalGuardian: "Legal guardian",
    legalGuardianShort: "LG",
    legalGuardianHelper: "Can make legal decisions for the student.",
    streetAddress: "Street address",
    streetAddressPlaceholder: "Start typing to search for an address...",
    addressSearching: "Searching addresses...",
    postalCode: "Postal code",
    city: "City",
    country: "Country",
    dateOfBirth: "Date of birth",
    genderIdentity: "Gender",
    genderPlaceholder: "Select gender",
    validation: {
      studentGuardian:
        "A student cannot be marked as their own legal guardian.",
      invalidPersonalNumber:
        "Enter a valid personal identity number (e.g. YYYYMMDD-XXXX)",
      invalidPhone:
        "Enter a valid phone number (7-30 characters: digits, spaces, +, -, ())",
      invalidPostalCode: "Enter a valid postal code for the selected country",
      studentAlreadyExists:
        "A student contact already exists. Only one student per person is allowed.",
      studentRelationLocked:
        "The Student relation type cannot be changed once set.",
    },
    primaryContact: "Primary contact",
    primaryBadge: "Primary",
    primaryContactHelper: "Contact this person first when needed.",
    primaryContactWillReplace:
      "This will replace {{name}} as the primary contact.",
    settings: "Contact Settings",
    call: "Call",
    sendEmail: "Send email",
    delete: "Delete",
    personalId: "Personal ID",
    deleteConfirmTitle: "Delete contact?",
    deleteConfirmMessage:
      "Are you sure you want to delete {{name}}? This action cannot be undone.",
    confirmDelete: "Delete",
    deletedSuccessfully: "Contact deleted successfully.",
    primaryContactUpdated: "Primary contact updated.",
    viewDetails: "View details",
    makePrimary: "Make primary",
    makePrimaryConfirmTitle: "Make primary contact?",
    moreOptions: "More options",
    save: "Save",
    saveChanges: "Save Changes",
    cancel: "Cancel",
    noContacts: "No contact cards added yet",
    noContactsHint:
      "Add a parent, guardian, sibling, or other contact for this student.",
    noContactInfo: "No contact info",
    discardTitle: "Discard changes?",
    discardMessage:
      "You have unsaved changes. Are you sure you want to leave without saving?",
    continueEditing: "Continue Editing",
    discardChanges: "Discard Changes",
    relationType: {
      student: "Student",
      parent: "Parent",
      guardian: "Guardian",
      sibling: "Sibling",
      relative: "Relative",
      foster_parent: "Foster parent",
      case_worker: "Case worker",
      other: "Other",
    },
    protected: "Protected",
    revealedBadge: "Shown",
    protectedSection: "Privacy",
    protectedToggle: "Protected personal data",
    protectedToggleHelper: "Data is hidden and access is logged",
    viewProtectedData: "Show details – access is logged",
    confirmViewTitle: "View protected data?",
    confirmViewContactMessage:
      "{{name}} has protected personal data. Your access will be logged in the event log with your name and timestamp.",
    confirmViewContactMessageSuffix:
      "has protected personal data. Your access will be logged in the event log with your name and timestamp.",
    confirmViewStudentMessage:
      "This student has protected personal data. If you choose to view the data, your access will be logged in the event log with your name and timestamp.",
    confirmEditTitle: "Edit protected data?",
    confirmEditContactMessageSuffix:
      "has protected personal data. Your access will be logged in the event log with your name and timestamp before editing.",
    viewAndLog: "View and log access",
    editAndLog: "Edit and log access",
    protectedDataBadge: "Protected data",
    protectedBannerActiveTitle: "Protected personal data",
    protectedBannerActiveDesc: "Data shown. Access is logged.",
    protectedBannerInactiveTitle: "Protected personal data",
    protectedBannerInactiveDesc:
      "Personal data is protected. Only first name is shown.",
    showProtectedData: "Show data",
    dismissBanner: "Dismiss",
    protectedCount: "{{count}} protected",
    eventLog: "Event log",
    eventLogTriggerHint: "View access history",
    eventLogSubtitle: "Access to protected personal data",
    eventLogTotal: "Total events",
    eventLogProfileEvents: "Student reveals",
    eventLogContactEvents: "Card-specific events",
    eventLogMarkerEvents: "Student markers",
    eventLogHearingScreeningEvents: "Hearing screening",
    eventLogVisionScreeningEvents: "Vision screening",
    eventLogGrowthMeasurementEvents: "Growth measurement",
    eventLogReferralEvents: "Referrals",
    eventLogTargetProfile: "Profile target",
    eventLogTargetCard: "Card target",
    eventLogTargetUnknown: "Target",
    eventLogStudentProfile: "Student profile",
    eventLogStudentCard: "Student card",
    eventLogContactCard: "Contact card",
    eventLogDeletedContact: "Contact not available",
    eventLogDeletedContactCard: "Deleted contact card",
    eventLogMarkerCard: "Student marker",
    eventLogHearingScreeningCard: "Hearing screening",
    eventLogVisionScreeningCard: "Vision screening",
    eventLogGrowthMeasurementCard: "Growth measurement",
    eventLogReferralCard: "Referral",
    eventLogMarkerVisibleToLabel: "Visible to",
    eventLogMarkerValidityLabel: "Validity period",
    eventLogMarkerValidityRange: "From {{from}} to {{until}}",
    eventLogMarkerValidFrom: "Valid from {{from}}",
    eventLogMarkerValidUntil: "Valid until {{until}}",
    eventLogNoFilterMatches: "No events match this filter.",
    eventLogClearFilter: "Clear filter",
    eventLogBy: "By {{name}}",
    viewedProtectedData: "Viewed protected personal data",
    gdprNote: "Log stored for 5 years according to GDPR and cannot be deleted.",
    noLogEntries: "No access events logged yet.",
    eventType: {
      viewProtectedStudent: "Viewed protected student data",
      viewProtectedContact: "Viewed protected contact data",
      activateProtection: "Activated identity protection",
      deactivateProtection: "Deactivated identity protection",
      addContact: "Added contact",
      editContact: "Edited contact",
      deleteContact: "Deleted contact",
      addStudentMarker: "Added student marker",
      editStudentMarker: "Edited student marker",
      archiveStudentMarker: "Archived student marker",
      createHearingScreening: "Added hearing screening",
      updateHearingScreeningDraft: "Updated hearing screening (draft)",
      signHearingScreening: "Signed hearing screening",
      unlockHearingScreening: "Unlocked hearing screening",
      updateHearingScreeningFinalAssessment:
        "Updated hearing screening assessment",
      removeHearingScreening: "Removed hearing screening",
      exportHearingScreening: "Exported hearing screening",
      createVisionScreening: "Added vision screening",
      updateVisionScreeningDraft: "Updated vision screening (draft)",
      signVisionScreening: "Signed vision screening",
      unlockVisionScreening: "Unlocked vision screening",
      updateVisionScreeningFinalAssessment:
        "Updated vision screening assessment",
      removeVisionScreening: "Removed vision screening",
      exportVisionScreening: "Exported vision screening",
      createGrowthMeasurement: "Added growth measurement",
      updateGrowthMeasurementDraft: "Updated growth measurement (draft)",
      signGrowthMeasurement: "Signed growth measurement",
      unlockGrowthMeasurement: "Unlocked growth measurement",
      removeGrowthMeasurement: "Removed growth measurement",
      exportGrowthMeasurement: "Exported growth measurement",
      createReferral: "Created referral",
      updateReferralDraft: "Updated referral draft",
      signReferral: "Signed referral",
      unlockReferral: "Unlocked referral",
      sendReferral: "Marked referral as sent",
      receiveReferralResponseUploaded: "Uploaded referral response",
      receiveReferralResponseRegistered: "Registered referral response",
      closeReferral: "Closed referral",
      cancelReferral: "Cancelled referral",
      removeReferral: "Deleted referral draft",
      exportReferral: "Downloaded referral PDF",
      unknown: "Unknown event",
    },
  },
  growthMeasurement: {
    sectionTitle: "Growth",
    registerMeasurement: "Register measurement",
    export: "Export",
    noMeasurements: "No growth measurements have been registered yet.",
    table: {
      date: "Date",
      height: "Height",
      weight: "Weight",
      bmi: "BMI",
      type: "Type",
      status: "Status",
      by: "By",
    },
    pdf: {
      exported: "Exported",
      measurementsSection: "Measurements",
      totalMeasurements: "Total measurements",
      latestHeight: "Latest height",
      latestWeight: "Latest weight",
      latestBmi: "Latest BMI",
      puberty: {
        sectionTitle: "Puberty assessment",
        hint: "Tanner stages are given as I–V. Testicular volume in ml.",
        none: "No puberty assessment has been registered.",
        breast: "Tanner breast",
        genital: "Tanner genital",
        pubicHair: "Tanner pubic hair",
        testicularLeft: "Testis left",
        testicularRight: "Testis right",
        menarche: "Menarche",
      },
      chart: {
        titleHeightWeight: "Growth chart – height and weight",
        titleBmi: "Growth chart – BMI",
        ageAtExport: "Age at export: {{age}}",
        reference: "Reference: {{reference}}",
        legendTitle: "Legend",
        legend: {
          leftAxis: "left axis",
          rightAxis: "right axis",
          median: "0 SD – mean",
          trend: "Progression between measurements",
          currentAge: "Age at export",
        },
        explanationHeightWeight:
          "The curves show reference values for height and weight by age: the mean (0 SD) and ±1, ±2 and ±3 standard deviations (SD). The dots are the student's registered measurements, placed at the student's exact age on the measurement date. Read height against the left axis and weight against the right axis.",
        explanationBmi:
          "The curves show reference values for BMI by age: the mean (0 SD) and ±1, ±2 and ±3 standard deviations (SD). The dots are the student's registered BMI values, placed at the student's exact age on the measurement date.",
        parents: "Biological mother: {{mother}}   ·   Biological father: {{father}}",
        midParentalHeight:
          "Target height (MPH): {{value}} cm (parents' mean height ± 6.5 cm depending on sex)",
        disclaimer:
          "The curves are intended for visual assessment. The export contains no calculated SDS values or percentiles.",
        source: "Source",
        notAvailableSex:
          "The growth chart was not included because the student's sex is not recorded as Male or Female.",
        notAvailable: "The growth chart could not be included in the export.",
      },
    },
    measurementType: {
      routine: "Routine measurement",
      followUp: "Follow-up",
      extraCheck: "Extra check",
      imported: "Imported",
    },
    bmiGauge: {
      title: "BMI",
      autoCalculated: "BMI is calculated automatically",
      fillHeightAndWeight: "Fill in height and weight",
      disclaimer:
        "This BMI calculation is for adults. For children and adolescents, BMI should be assessed according to age and sex.",
      shortLabel: {
        severeUnderweight: "Severe",
        underweight: "Under",
        normal: "Normal",
        overweight: "Over",
        obesity: "Obese",
      },
    },
    bmiCategory: {
      severeUnderweight: "Severe underweight",
      underweight: "Underweight",
      normal: "Normal weight",
      overweight: "Overweight",
      obesity: "Obesity",
    },
    units: {
      cm: "cm",
      kg: "kg",
      ml: "ml",
      kgPerM2: "kg/m²",
    },
    chart: {
      open: "Growth chart",
      title: "Growth chart",
      currentAge: "Current age: {{age}}",
      ageYearsMonths: "{{years}} y {{months}} m",
      sexLabel: "Sex",
      referenceLabel: "Reference",
      viewLabel: "Chart view",
      print: "Print",
      printComingSoon: "Printing becomes available once the chart is in place",
      referenceSource: "Reference: {{reference}}",
      sex: {
        female: "Girl",
        male: "Boy",
      },
      references: {
        who519: "WHO 5–19",
        who519Short: "WHO 5–19",
        swe81: "Sweden 1981",
        swe81Short: "Sweden 81",
      },
      views: {
        heightWeight: "Height + weight",
        bmi: "BMI",
      },
      metrics: {
        height: "Height",
        weight: "Weight",
        bmi: "BMI",
      },
      legend: {
        median: "Mean",
        measurements: "Measurements",
        latest: "Latest",
        previous: "Previous",
        trend: "Trend",
      },
      display: {
        title: "Show",
        currentAge: "Current age",
        parentHeight: "Parental height",
        measurementTable: "Measurement table",
      },
      parentHeight: {
        title: "Parental height",
        mother: "Biological mother",
        father: "Biological father",
        midParentalHeight: "Target height (MPH)",
        sexRequired: "Select sex to calculate the target height",
        localOnlyHint:
          "Prefilled from the latest registered measurement. Changes here are not saved.",
      },
      axis: {
        age: "Age (years)",
        currentAge: "Age",
        midParentalHeight: "MPH {{value}}",
      },
      tooltip: {
        type: "Measurement type",
      },
      states: {
        loadError: "The reference curves could not be loaded. Please try again.",
        selectSex: "Select sex to show the correct reference curves.",
        missingReference: "No reference curve for the selected combination.",
        missingMetricReference:
          "No {{metric}} reference curve in {{reference}}.",
        noMeasurements: "No measurements registered for the student.",
        noBirthDate:
          "Date of birth is missing – the student's measurements can't be placed on the chart.",
      },
      table: {
        age: "Age",
        sds: "SDS",
      },
    },
    wizard: {
      title: "New growth measurement",
      titleEdit: "Edit growth measurement",
      previousMeasurements: "Previous measurements",
      previousMeasurementsCount: "{{count}} registered",
      noPreviousMeasurements: "No previous measurements",
      step1Title: "Basic measurement",
      step2Title: "Puberty",
      step3Title: "Parental data",
      step4Title: "Review",
      selectDate: "Select date",
      measuredAt: "Measurement date",
      measurementType: "Measurement type",
      heightCm: "Height",
      heightHint: "E.g. 154.5",
      weightKg: "Weight",
      weightHint: "E.g. 43.2",
      pubertyAssessed: "Puberty assessed",
      pubertyAssessedHint: "Turn on if a puberty assessment was carried out",
      tannerBreastStage: "Tanner stage – breast",
      tannerGenitalStage: "Tanner stage – genital",
      tannerPubicHairStage: "Tanner stage – pubic hair",
      testicularVolumeLeftMl: "Testicular volume left (ml)",
      testicularVolumeRightMl: "Testicular volume right (ml)",
      menarcheStatus: "Menarche status",
      menarcheDate: "Menarche date",
      biologicalMotherHeightCm: "Biological mother's height (cm)",
      motherHeightHint: "E.g. 162.0",
      biologicalFatherHeightCm: "Biological father's height (cm)",
      fatherHeightHint: "E.g. 178.0",
      midParentalHeightPreview: "Calculated target height (MPH)",
      midParentalHeightFormula:
        "Based on the biological parents' height and the child's sex",
      midParentalHeightEmpty:
        "Fill in both parents' height to calculate the target height",
      midParentalHeightGenderRequired:
        "Target height can only be calculated when the student's gender is set to Male or Female. Update it in the student's profile.",
      midParentalHeightProtected:
        "This student's data is protected. Reveal the student in their profile to calculate the target height.",
      note: "Clinical note",
      notePlaceholder: "Add a note...",
      saveDraft: "Save draft",
      saveAndSign: "Save and sign",
      savedDraftSuccess: "Draft saved.",
      signedSuccess: "Measurement signed.",
      lockedTitle: "This measurement is signed",
      lockedDescription:
        "This measurement is signed and cannot be edited. Unlock it to make changes.",
      unlockToEdit: "Unlock to edit",
      discardTitle: "Discard changes?",
      discardMessage:
        "You have unsaved changes. Are you sure you want to leave without saving?",
      continueEditing: "Continue Editing",
      discardChanges: "Discard Changes",
    },
    menarcheStatus: {
      notAssessed: "Not assessed",
      no: "No",
      yes: "Yes",
      unknown: "Unknown",
    },
    validation: {
      heightRange: "Enter a height between 30 and 250 cm",
      weightRange: "Enter a weight between 1 and 300 kg",
      parentHeightRange: "Enter a height between 70 and 230 cm",
      testicularVolumeRange: "Enter a volume between 1 and 100 ml",
    },
    sign: {
      title: "Sign measurement",
      description:
        "By signing, you confirm the data is correct. The measurement will then be locked for editing.",
      confirmLabel: "Type your name to confirm",
      nameMismatch: "The name doesn't match your account name.",
      cancel: "Cancel",
      confirm: "Sign",
    },
    unlock: {
      title: "Unlock measurement",
      description:
        "Enter a reason why this signed measurement needs to be unlocked for editing.",
      signedBy: "Signed by",
      reasonLabel: "Reason",
      reasonPlaceholder: "Describe why the measurement needs to be unlocked...",
      cancel: "Cancel",
      confirm: "Unlock",
    },
    detail: {
      title: "Growth measurement",
      edit: "Edit",
      sign: "Sign",
      unlock: "Unlock",
      export: "Export",
      measuredBy: "Measured by",
      midParentalHeight: "Calculated target height (MPH)",
      eventLog: "Event log",
      changelog: "Changelog",
      snapshotPreview: "Snapshot",
      deleteConfirmTitle: "Delete measurement?",
      deleteConfirmMessage:
        "Are you sure you want to delete this measurement? This action cannot be undone.",
      deletedSuccess: "Measurement deleted.",
      exportedSuccess: "Measurement exported.",
      unlockedSuccess: "Measurement unlocked.",
    },
    eventLog: {
      created: "Measurement created",
      updated_draft: "Draft updated",
      signed: "Measurement signed",
      unlocked: "Measurement unlocked",
      exported: "Measurement exported",
      removed: "Measurement removed",
    },
  },
  visionScreening: {
    sectionTitle: "Vision screening",
    registerScreening: "New vision screening",
    noScreenings: "No vision screenings have been registered yet.",
    table: {
      date: "Date",
      right: "Right",
      left: "Left",
      binocular: "Binocular",
      systemResult: "System result",
      finalAssessment: "Assessment",
      status: "Status",
    },
    contactType: {
      health_visit: "Health visit",
      reception_visit: "Reception visit",
      follow_up: "Follow-up",
      other: "Other",
    },
    correction: {
      none: "None",
      glasses: "Glasses",
      contact_lenses: "Contact lenses",
      unknown: "Unknown",
      not_tested: "Not tested",
    },
    testMethod: {
      km_chart: "KM chart",
      hvot_chart: "HVOT chart",
      evnd_chart: "EVND chart",
      snellen_rows: "Snellen rows",
      letter_rows: "Letter rows",
      lea_symbols: "LEA symbols",
      other: "Other",
    },
    eye: {
      right: "Right eye",
      left: "Left eye",
    },
    acuityCategory: {
      excellent: "Excellent",
      acceptable: "Acceptable",
      belowThreshold: "Below threshold",
    },
    resultStatus: {
      normal: "Normal",
      incomplete: "Incomplete",
      needs_retest: "Retest recommended",
      follow_up_needed: "Follow-up needed",
      refer_optician: "Refer to optician",
      refer_eye_clinic: "Refer to eye clinic",
    },
    finalAssessment: {
      normal: "Normal",
      retest: "Retest",
      follow_up_needed: "Follow-up needed",
      refer_optician: "Refer to optician",
      refer_eye_clinic: "Refer to eye clinic",
      primary_care: "Primary care",
      no_action: "No action",
    },
    referralTarget: {
      eye_clinic: "eye clinic",
      optician: "optician",
    },
    units: {
      m: "m",
    },
    form: {
      title: "New vision screening",
      titleEdit: "Edit vision screening",
      selectDate: "Select date",
      selectPlaceholder: "Select...",
      screenedAt: "Date",
      contactType: "Contact type",
      contactTypeRequired: "Contact type is required.",
      screeningDistanceMeters: "Test distance (m)",
      screeningDistancePlaceholder: "E.g. 5",
      screeningDistanceRange: "Enter a test distance between 0.5 and 20 m",
      screeningCompleted: "Completion status",
      completed: "Screening completed",
      notCompleted: "Not completed",
      reasonNotCompletedPlaceholder:
        "State the reason the screening could not be completed...",
      observations: "Observations",
      symptomsReported: "Symptoms reported",
      symptomsNotePlaceholder: "Describe the reported symptoms...",
      squintOrStrabismusObserved: "Squint/strabismus observed",
      previousEyeCareContact: "Previous eye care contact",
      note: "Note",
      notePlaceholder: "Add a note...",
      quickActions: "Quick actions",
      quickActionNormal: "Vision screening normal",
      quickActionBothToOne: "Set both eyes to 1.0",
      autoSummaryTitle: "Automatic summary",
      ageReferralHint:
        "Age {{age}} years (≥{{ageThreshold}} years) → {{target}}",
      testMethod: "Test method",
      testMethodRequired: "Test method is required.",
      testMethodOtherPlaceholder: "Enter test method",
      rightEye: "Right eye",
      leftEye: "Left eye",
      binocular: "Binocular",
      correction: "Correction",
      visualAcuity: "Visual acuity (decimal)",
      thresholdCaption: "Threshold: {{threshold}}",
      fieldLabel: {
        rightEyeVisualAcuity: "Right eye",
        leftEyeVisualAcuity: "Left eye",
      },
      saveDraft: "Save draft",
      saveAndSign: "Save and sign",
      savedDraftSuccess: "Draft saved.",
      signedSuccess: "Vision screening signed.",
      lockedTitle: "This vision screening is signed",
      lockedDescription:
        "This vision screening is signed and cannot be changed. Unlock it to edit.",
      unlockToEdit: "Unlock to edit",
      discardTitle: "Discard changes?",
      discardMessage:
        "You have unsaved changes. Are you sure you want to leave without saving?",
      continueEditing: "Continue editing",
      discardChanges: "Discard changes",
    },
    summary: {
      notCompleted: "The screening was not completed.",
      missingValues: "Missing values: {{fields}}.",
      normal:
        "Right and left visual acuity are both at or above {{threshold}}.",
      symptomsFlag:
        "Visual acuity is within the configured range, but symptoms/observations were documented. Nurse review recommended.",
      belowThreshold: "Values below the threshold ({{threshold}}): {{eyes}}.",
      interocularDifference:
        "Difference between eyes: {{diff}} (limit {{maxInterocularDifference}}). Right: {{right}}, left: {{left}}.",
      referralSuggestion:
        "Based on age and result: consider {{assessment}} according to local routine.",
    },
    sign: {
      title: "Sign vision screening",
      description:
        "By signing you confirm that the information is correct. The vision screening will then be locked for editing.",
      confirmLabel: "Type your name to confirm",
      nameMismatch: "The name doesn't match your account name.",
      cancel: "Cancel",
      confirm: "Sign",
    },
    unlock: {
      title: "Unlock vision screening",
      description:
        "State a reason why this signed vision screening needs to be unlocked for editing.",
      signedBy: "Signed by",
      reasonLabel: "Reason",
      reasonPlaceholder:
        "Describe why the vision screening needs to be unlocked...",
      cancel: "Cancel",
      confirm: "Unlock",
    },
    detail: {
      title: "Vision screening",
      edit: "Edit",
      sign: "Sign",
      unlock: "Unlock",
      screenedBy: "Performed by",
      reasonNotCompleted: "Reason the screening was not completed",
      testMethodOther: "Other test method",
      autoSummaryTitle: "Automatic screening summary",
      finalAssessmentTitle: "Nurse's assessment",
      finalAssessmentNotApplicable:
        "Cannot be assessed because the screening was not completed.",
      eventLog: "Event log",
      changelog: "Change history",
      deleteConfirmTitle: "Delete vision screening?",
      deleteConfirmMessage:
        "Are you sure you want to delete this vision screening? This action cannot be undone.",
      deletedSuccess: "Vision screening deleted.",
      unlockedSuccess: "Vision screening unlocked.",
      finalAssessmentUpdatedSuccess: "Assessment updated.",
    },
    eventLog: {
      created: "Vision screening created",
      updated_draft: "Draft updated",
      signed: "Vision screening signed",
      unlocked: "Vision screening unlocked",
      exported: "Vision screening exported",
      removed: "Vision screening removed",
      final_assessment_updated: "Assessment updated",
    },
  },
  hearingScreening: {
    sectionTitle: "Hearing screening",
    registerScreening: "New hearing screening",
    noScreenings: "No hearing screenings have been registered yet.",
    table: {
      date: "Date",
      systemResult: "System result",
      finalAssessment: "Assessment",
      status: "Status",
      performedBy: "Performed by",
    },
    contactType: {
      health_visit: "Health visit",
      reception_visit: "Reception visit",
      follow_up: "Follow-up",
      other: "Other",
    },
    screeningType: {
      routine: "Routine check",
      follow_up: "Follow-up",
      extra_check: "Extra check",
      imported: "Imported",
    },
    ear: {
      right: "Right ear",
      left: "Left ear",
      rightLower: "right ear",
      leftLower: "left ear",
    },
    resultStatus: {
      normal: "Normal",
      incomplete: "Incomplete",
      minor_deviation: "Minor deviation",
      needs_retest: "Needs retest",
      follow_up_needed: "Follow-up needed",
      referred: "Referred",
    },
    finalAssessment: {
      normal: "Normal",
      needs_retest: "Needs retest",
      follow_up_needed: "Follow-up needed",
      referred: "Referred",
      no_action: "No action",
    },
    units: {
      hz: "Hz",
      dbHl: "dB HL",
    },
    audiogram: {
      rightEar: "Right ear · O",
      leftEar: "Left ear · X",
      rightEarShort: "R",
      leftEarShort: "L",
      thresholdLine: "{{threshold}} dB limit",
      aboveThreshold: "Above screening threshold",
      frequencyAxis: "Frequency (Hz)",
    },
    form: {
      title: "New hearing screening",
      titleEdit: "Edit hearing screening",
      selectDate: "Select date",
      selectPlaceholder: "Select...",
      screeningDetailsTitle: "Screening details",
      screenedAt: "Date",
      screeningThresholdDb: "Screening threshold",
      contactType: "Contact type",
      screeningType: "Screening type",
      note: "Note",
      notePlaceholder: "Free text...",
      frequency: "Frequency (Hz)",
      optional: "optional",
      resultFieldLabel: "{{ear}}, {{frequency}} Hz, dB HL",
      showAdditionalFrequencies: "Show additional frequencies: {{list}}",
      hideAdditionalFrequencies: "Hide additional frequencies",
      additionalResultsHiddenNote_one: "{{count}} additional result recorded",
      additionalResultsHiddenNote_other:
        "{{count}} additional results recorded",
      completionCount: "{{recorded}} of {{total}} required results recorded",
      completionHintEmpty: "Enter results for both ears.",
      completionHintComplete: "All required results recorded",
      viewMissing: "View missing",
      hideMissing: "Hide missing",
      missingListTitle: "Missing required results",
      dateRequired: "Select a screening date.",
      thresholdRequired: "Screening threshold is required.",
      contactTypeRequired: "Select a contact type.",
      screeningTypeRequired: "Select a screening type.",
      missingResultFor: "Enter a result for the {{ear}} at {{frequency}} Hz.",
      valueRangeError: "Value must be between {{min}} and {{max}} dB HL.",
      errorSummaryTitle_one: "Fix the following issue before you can sign:",
      errorSummaryTitle_other:
        "Fix the following {{count}} issues before you can sign:",
      quickActions: "Quick actions",
      quickActionFillEmpty: "Fill empty results with {{threshold}} dB HL",
      quickActionRecordNormal:
        "Record normal results for all required frequencies ({{threshold}} dB HL)",
      quickActionOverwriteAll: "Overwrite all with {{threshold}} dB HL",
      quickActionOverwriteConfirmTitle: "Overwrite existing results?",
      quickActionOverwriteConfirmMessage:
        "This sets {{count}} field(s) across {{frequencyCount}} frequencies to {{threshold}} dB HL for both ears, replacing {{existingCount}} existing result(s). You can undo right after.",
      quickActionRecordNormalConfirmTitle: "Replace existing required results?",
      quickActionRecordNormalConfirmMessage:
        "This sets all {{count}} required results to {{threshold}} dB HL for both ears, replacing {{existingCount}} existing result(s). The assessment field is not changed. You can undo right after.",
      quickActionConfirmCta: "Yes, set values",
      bulkEditAppliedMessage_one:
        "Set {{count}} result to {{threshold}} dB HL.",
      bulkEditAppliedMessage_other:
        "Set {{count}} results to {{threshold}} dB HL.",
      bulkEditNothingToFill: "All visible results already have a value.",
      undo: "Undo",
      undoAppliedMessage_one: "Reverted {{count}} result.",
      undoAppliedMessage_other: "Reverted {{count}} results.",
      autoSummaryTitle: "Automatic summary",
      finalAssessment: "Assessment (nurse)",
      tabValues: "Values",
      tabAudiogram: "Audiogram",
      saveDraft: "Save draft",
      reviewAndSign: "Review & sign",
      reviewIntro:
        "Check the details below, then sign to lock this screening for editing.",
      reviewBackToEdit: "Back to edit",
      reviewSignButton: "Sign screening",
      footerStatusSaving: "Saving…",
      footerStatusUnsaved: "Unsaved changes",
      savedDraftSuccess: "Draft saved.",
      signedSuccess: "Hearing screening signed.",
      lockedTitle: "This hearing screening is signed",
      lockedDescription:
        "This hearing screening is signed and cannot be changed. Unlock it to edit.",
      unlockToEdit: "Unlock to edit",
      discardTitle: "Discard changes?",
      discardMessage:
        "You have unsaved changes. Are you sure you want to leave without saving?",
      continueEditing: "Continue editing",
      discardChanges: "Discard changes",
    },
    summary: {
      normal:
        "All required frequencies were heard at {{threshold}} dB HL or better.",
      missingIntro: "Missing required values:",
      minorDeviationIntro:
        "One frequency was above the screening threshold but within the allowed local exception rule.",
      needsRetestIntro:
        "The following values are above the configured screening threshold:",
      thresholdLine: "Configured threshold: {{threshold}} dB HL",
      retestSuggestion:
        "Consider retest or follow-up according to local routine.",
    },
    sign: {
      title: "Sign hearing screening",
      description:
        "By signing you confirm that the information is correct. The hearing screening will then be locked for editing.",
      confirmLabel: "Type your name to confirm",
      nameMismatch: "The name doesn't match your account name.",
      cancel: "Cancel",
      confirm: "Sign",
    },
    unlock: {
      title: "Unlock hearing screening",
      description:
        "State a reason why this signed hearing screening needs to be unlocked for editing.",
      signedBy: "Signed by",
      reasonLabel: "Reason",
      reasonPlaceholder:
        "Describe why the hearing screening needs to be unlocked...",
      cancel: "Cancel",
      confirm: "Unlock",
    },
    detail: {
      title: "Hearing screening",
      edit: "Edit",
      sign: "Sign",
      unlock: "Unlock",
      screeningDate: "Screening date",
      screenedBy: "Performed by",
      threshold: "Threshold",
      audiogram: "Audiogram",
      values: "Hearing values",
      autoSummaryTitle: "Automatic screening summary",
      finalAssessmentTitle: "Nurse's assessment",
      signed: "Signed",
      eventLog: "Event log",
      changelog: "Change history",
      deleteConfirmTitle: "Delete hearing screening?",
      deleteConfirmMessage:
        "Are you sure you want to delete this hearing screening? This action cannot be undone.",
      deletedSuccess: "Hearing screening deleted.",
      unlockedSuccess: "Hearing screening unlocked.",
      finalAssessmentUpdatedSuccess: "Assessment updated.",
    },
    eventLog: {
      created: "Hearing screening created",
      updated_draft: "Draft updated",
      signed: "Hearing screening signed",
      unlocked: "Hearing screening unlocked",
      exported: "Hearing screening exported",
      removed: "Hearing screening removed",
      final_assessment_updated: "Assessment updated",
    },
  },
  studentMarker: {
    bannerTitle: "Markers",
    addMarker: "Add marker",
    archiveMarker: "Archive marker",
    archiveTitle: "Archive marker?",
    archiveMessage:
      'Archiving "{{title}}" removes it from the profile. It stays in the activity log and can be restored later.',
    archiveConfirm: "Archive",
    archiveSuccess: "Marker archived.",
    noEndDate: "No end date",
    protectedLockPrompt:
      "This student's profile is protected. Click to view markers.",
    visibleTo: "Visible to",
    visibleToHint:
      "Only users with access to the selected journal can see this marker.",
    visibleToRestrictedCount_one: "{{count}} restricted journal",
    visibleToRestrictedCount_other: "{{count}} restricted journals",
    form: {
      titleCreate: "Add marker",
      subtitle:
        "Shown at the top of the student's profile for authorized users",
      titleLabel: "Title",
      titlePlaceholder: "E.g. peanut allergy, Epipen required...",
      descriptionLabel: "Description",
      descriptionOptionalLabel: "Description (optional)",
      descriptionPlaceholder: "Additional information...",
      typeLabel: "Type",
      severityLabel: "Severity",
      advancedSectionTitle: "Advanced — validity period",
      validFromLabel: "Valid from",
      validUntilLabel: "Valid until",
      sourceTypeLabel: "Source",
      cancel: "Cancel",
      save: "Save marker",
      discardTitle: "Discard changes?",
      discardMessage:
        "You have unsaved changes. Are you sure you want to leave without saving?",
      continueEditing: "Continue editing",
      discardChanges: "Discard changes",
    },
    type: {
      allergy: "Allergy",
      hypersensitivity: "Hypersensitivity",
      medical_warning: "Medical warning",
      information: "Information",
    },
    severity: {
      low: "Low",
      high: "High",
    },
    priority: {
      low: "Low priority",
      high: "High priority",
    },
    sourceType: {
      manual: "Manual",
      imported: "Imported",
      other: "Other",
    },
    validation: {
      journalTypeIdsRequired: "Select at least one journal folder.",
      validUntilBeforeValidFrom:
        '"Valid until" must be on or after "Valid from".',
      titleMaxLength: "Maximum 250 characters are allowed.",
    },
  },
  customReport: {
    heading: "You have been tagged in a custom report",
    message: '{{taggerName}} tagged you in "{{reportName}}"',
  },
  consent: {
    common: {
      title: "Consents",
      create: "Create consent",
      back: "Back",
      next: "Next",
      cancel: "Cancel",
      save: "Save",
      close: "Close",
      confirm: "Confirm",
      studentsSelected: "{{count}} students selected",
      studentsSelected_one: "{{count}} student selected",
      studentsSelected_other: "{{count}} students selected",
      downloadPdf: "Download PDF",
      loading: "Loading…",
      optional: "Optional",
      none: "—",
    },
    list: {
      subtitle: "All consents – manual, uploaded, approved and digital",
      searchPlaceholder: "Search student or title…",
      allMethods: "All methods",
      moreFilters: "More filters",
      fewerFilters: "Fewer filters",
      clearFilters: "Clear filters",
      template: "Template",
      allTemplates: "All templates",
      attribute: "Class / attribute",
      allAttributes: "All attributes",
      createdBy: "Created by",
      anyone: "Anyone",
      dateFrom: "From",
      dateTo: "To",
      emptyTitle: "No consents yet",
      emptyText: "Create a consent for one student or for a whole class.",
      noMatchTitle: "No matching consents",
      noMatchText: "Try another filter or search.",
      columns: {
        title: "Title",
        student: "Student",
        createdFor: "Created for",
        method: "Method",
        recipients: "Recipients",
        status: "Status",
        createdBy: "Created by",
        createdAt: "Created",
        updatedAt: "Updated",
      },
      showing: "Showing {{from}}–{{to}} of {{total}}",
      previous: "Previous",
      nextPage: "Next",
    },
    stats: {
      total: "Total",
      pending: "Waiting",
      done: "Done",
      needsAction: "Needs action",
    },
    statusFilters: {
      all: "All",
      pendingDigital: "Awaiting signature",
      pendingManual: "Awaiting return",
      signed: "Signed",
      approved: "Approved",
      denied: "Denied",
      failed: "Failed",
    },
    statuses: {
      draft: "Draft",
      pending_manual: "Awaiting return",
      pending_digital: "Awaiting signature",
      approved_manual: "Approved manually",
      signed_digital: "Signed digitally",
      denied: "Denied",
      expired: "Expired",
      cancelled: "Cancelled",
      failed: "Failed",
    },
    methods: {
      manual_print: "Print",
      manual_upload: "Uploaded",
      manual_approval: "Manual",
      digital_bankid: "Digital BankID",
    },
    methodCards: {
      manual_print: {
        title: "Print / physical consent",
        text: "Generate a PDF for printing. Scan and upload the signed consent later.",
      },
      manual_upload: {
        title: "Upload already signed consent",
        text: "You already have a scanned, signed consent to register.",
      },
      manual_approval: {
        title: "Approve manually without a file",
        text: "Consent received outside Allbry (e.g. phone or meeting).",
      },
      digital_bankid: {
        title: "Digital consent with BankID",
        text: "Send to guardians for digital signing via Inleed DocSign.",
      },
      resultsIn: "Results in",
    },
    createdFor: {
      individual: "Individual",
      attribute: "Attribute",
    },
    approvalSources: {
      paper: "Paper",
      scanned_file: "Scanned file",
      phone: "Phone call",
      email: "Email",
      meeting: "Meeting",
      other: "Other",
      digital_bankid: "Digital BankID",
    },
    signerRelations: {
      guardian_1: "Guardian 1",
      guardian_2: "Guardian 2",
      custom: "Recipient",
    },
    signerStatuses: {
      pending: "Waiting",
      signed: "Signed {{date}}",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    contactRelations: {
      guardian: "Guardian",
      parent: "Parent",
      foster_parent: "Foster parent",
      student: "Student",
      other: "Other",
    },
    journalTypeDescriptions: {
      medical: "Vaccinations, health visits and medical interventions.",
      psychosocial: "Counselling, conversations and psychosocial support.",
      general: "Other student health work that is not confidential care.",
    },
    journalTypes: {
      medical: "Medical record",
      psychosocial: "Psychosocial record",
      general: "General record",
    },
    fields: {
      template: "Template / title",
      title: "Title",
      description: "Description",
      internalNote: "Internal note",
      internalNoteHint: "Only visible internally",
      expiresAt: "Valid until",
      method: "Method",
      recipients: "Recipients",
      createdBy: "Created by",
      createdAt: "Created",
      updatedAt: "Updated",
      requiresBothGuardians: "Both guardians required",
      approvedBy: "Approved by",
      approvedAt: "Approved",
      approvalSource: "Source",
      approvalNote: "Note",
      deniedBy: "Denied by",
      deniedAt: "Denied",
      denialNote: "Note",
      journal: "Journal",
      yes: "Yes",
      no: "No",
    },
    wizard: {
      title: "Create consent",
      stepOf: "Step {{step}} of {{total}}",
      steps: {
        students: "Select students",
        template: "Template / text",
        method: "Handling",
        journal: "Journal",
      },
      tabs: {
        individual: "Individual students",
        attribute: "Attribute (class)",
      },
      studentsHint: "Select the students the consent concerns.",
      studentFromProcess: "The consent is created for the student in this process.",
      searchStudent: "Search student…",
      noStudents: "No students found",
      attributesHint: "Filter students by a shared attribute",
      addAttributes: "Add by attribute",
      selectedAttributes: "Selected attributes",
      noAttributesSelected: "No attributes selected",
      studentCount: "{{count}} students",
      studentCount_one: "{{count}} student",
      studentCount_other: "{{count}} students",
      preview: "Preview",
      customTitlePlaceholder: "E.g. Consent for excursion",
      customDescriptionPlaceholder: "Describe what the guardian consents to…",
      uploadTypes: "PDF or image",
      uploadSingleOnly: "Upload is only available when one student is selected.",
      confirmManual: "I confirm that consent has been received and registered manually.",
      signers: "Signers",
      guardianCounts: {
        1: "One guardian",
        2: "Two guardians",
      },
      recipientSource: "Recipients",
      recipientHint:
        "Pick guardians from the contact cards, add other recipients, or combine both.",
      recipientCount: "{{selected}} / {{count}} selected",
      otherRecipients: "Other recipients",
      addRecipient: "Add recipient",
      noGuardian: "No guardian with legal custody on the contact card",
      notEnoughGuardians: "Only one guardian with legal custody on the contact card",
      missingEmail: "Missing email",
      recipientName: "Name",
      recipientEmail: "Email",
      noValidRecipient: "Enter a valid recipient to send digitally.",
      journalHint: "Choose which journal the consent is registered in.",
      submit: "Create consent",
      preparingPdfs: "Preparing documents {{done}} / {{total}}…",
      resultTitle: "Consents created",
      resultCreated: "{{count}} created",
      resultFailed: "{{count}} failed",
      resultDownloadHint: "The PDF is generated when you click Download PDF.",
      done: "Done",
    },
    detail: {
      notFound: "The consent could not be found",
      backToList: "Back to consents",
      info: "Information",
      approval: "Approval",
      denial: "Denial",
      consentText: "Consent text",
      signers: "Signers",
      noSigners: "No signers",
      documents: "Documents",
      noDocuments: "No documents yet.",
      originalPdf: "Original PDF",
      signedPdf: "Signed PDF",
      uploadedScan: "Uploaded consent",
      actions: "Actions",
      history: "Status history",
      noHistory: "No events yet",
      createdForAttribute: "Attribute: {{attributes}}",
    },
    actions: {
      uploadSigned: "Upload signed consent",
      approve: "Approve manually",
      deny: "Mark as denied",
      cancel: "Cancel consent",
      retry: "Try again (digital)",
      switchToManual: "Switch to manual",
      sync: "Sync status",
      downloadSigned: "Download signed PDF",
      downloadUploaded: "Download uploaded file",
      delete: "Delete",
    },
    modals: {
      approveTitle: "Approve consent manually",
      denyTitle: "Mark consent as denied",
      deniedByName: "Denied by (name)",
      cancelTitle: "Cancel consent",
      cancelText: "The consent is cancelled and pending signers are removed.",
      reason: "Reason",
      uploadTitle: "Upload signed consent",
      deleteTitle: "Delete consent",
      deleteText: "The consent is removed from the list. Its history is kept.",
      retryTitle: "Send digitally again",
      switchTitle: "Switch to manual handling",
      switchText: "The consent becomes a printed consent awaiting return.",
      retryText: "A new PDF is generated and sent again to the same signers for signing with BankID.",
    },
    events: {
      created: "Created",
      pdf_generated: "PDF downloaded",
      sent_digital: "Sent for digital signing",
      uploaded_file: "Signed consent uploaded",
      approved_manual: "Approved manually",
      signed_digital: "Signed digitally",
      denied: "Denied",
      cancelled: "Cancelled",
      expired: "Expired",
      synced: "Status synced",
      party_signed: "Signed by {{name}}",
      callback_received: "Update received",
      deleted: "Deleted",
      switched_to_manual: "Switched to manual",
      sendFailed: "Digital send failed",
      byProvider: "Inleed DocSign",
      bySystem: "System",
    },
    success: {
      created: "Consents created",
      approved: "Consent approved",
      denied: "Consent marked as denied",
      cancelled: "Consent cancelled",
      uploaded: "Signed consent uploaded",
      retried: "Consent sent",
      switched: "Consent switched to manual handling",
      synced: "Status synced",
      deleted: "Consent deleted",
    },
    eventLog: {
      card: "Consent",
      events: "Consent events",
    },
    pdf: {
      heading: "Consent request",
      requestedAt: "Requested",
      consentId: "Consent ID",
      student: "Student",
      name: "Name",
      birthday: "Date of birth",
      personalIdentity: "Personal identity number",
      classGroup: "Class / group",
      school: "School",
      guardian: "Guardian",
      signer: "Signer",
      relation: "Relation",
      email: "Email",
      phone: "Phone",
      consent: "Consent",
      validity: "Validity",
      decision: "Decision",
      approve: "I consent",
      deny: "I do not consent",
      signature: "Signature",
      signatureLine: "Signature",
      datePlace: "Date and place",
      nameClarification: "Name in block letters",
      guardianName: "Guardian name",
      digitalSignature: "Signed digitally with BankID via Inleed DocSign.",
      record: "Registration",
      status: "Status",
      approvedAt: "Approved at",
      deniedAt: "Denied at",
      signingStatus: "Signing status",
      signedBy: "Signed by",
      requestedBy: "Requested by",
      role: "Role",
      generatedBy: "Generated by",
      footerGenerated: "This consent was generated via Allbry.",
      footerDigital: "This consent is sent for digital signing via Allbry.",
    },
    validation: {
      titleRequired: "Enter a title.",
      titleTooLong: "The title can be at most 255 characters.",
      descriptionRequired: "Enter a description.",
      fileRequired: "Upload the signed consent (PDF or image).",
      confirmRequired: "Confirm that the consent has been received.",
      emailRequired: "Enter an email address.",
      emailInvalid: "Enter a valid email address.",
      emailDuplicate: "This email is already a recipient for this student.",
    },
    errors: {
      generic: "Something went wrong. Please try again.",
      pdfFailed: "The PDF could not be generated.",
      CONSENT_NO_PERMISSION: "You don't have permission to do this.",
      CONSENT_NOT_FOUND: "The consent could not be found.",
      CONSENT_STUDENT_NOT_FOUND: "The student could not be found.",
      CONSENT_NO_STUDENTS_SELECTED: "Select at least one student.",
      CONSENT_NO_STUDENTS_FOR_ATTRIBUTE: "No students have the selected attribute.",
      CONSENT_TEMPLATE_NOT_FOUND: "The template could not be found.",
      CONSENT_TITLE_REQUIRED: "Enter a title.",
      CONSENT_DESCRIPTION_REQUIRED: "Enter a description.",
      CONSENT_FILE_REQUIRED: "A file is required.",
      CONSENT_UPLOAD_SINGLE_STUDENT_ONLY: "Upload is only available for one student.",
      CONSENT_CONFIRMATION_REQUIRED: "Confirm that the consent has been received.",
      CONSENT_SIGNER_EMAIL_REQUIRED: "Enter a valid recipient to send digitally.",
      CONSENT_SIGNER_EMAIL_DUPLICATE: "The same email can only be used once per student.",
      CONSENT_INVALID_STATUS_TRANSITION: "The action is not allowed in the current status.",
      CONSENT_INVALID_JOURNAL_TYPE: "Choose a valid journal.",
      CONSENT_INLEED_NOT_CONFIGURED: "Digital signing is not configured.",
      CONSENT_INLEED_PARTY_FAILED: "The signer could not be registered with Inleed.",
      CONSENT_INLEED_DOCUMENT_FAILED: "The document could not be sent to Inleed.",
      CONSENT_SIGNED_PDF_NOT_AVAILABLE: "The signed PDF is not available yet.",
      CONSENT_PROCESS_SINGLE_STUDENT_ONLY: "A consent from a process step can only be created for one student.",
      CONSENT_PROCESS_TASK_NOT_FOUND: "The process step could not be found.",
      fileTooLarge: "The file is too large (max 20 MB).",
      invalidFileType: "Only PDF or image files (PNG, JPG) can be uploaded.",
    },
  },
  referral: {
    common: {
      select: "Select...",
      back: "Back",
      next: "Next",
      cancel: "Cancel",
      save: "Save",
      edit: "Edit",
      remove: "Remove",
      optional: "optional",
    },
    status: {
      draft: "Draft",
      signed: "Signed",
      sent: "Sent",
      response_received: "Response received",
      closed: "Closed",
      cancelled: "Cancelled",
      deleted: "Deleted",
    },
    sentMethods: {
      post: "Post",
      handed_over: "Handed over",
      secure_email: "Secure email",
      other: "Other",
    },
    recipientTypes: {
      bup: "BUP",
      health_center: "Health center",
      specialist_clinic: "Specialist clinic",
      speech_therapy: "Speech therapy",
      other: "Other",
    },
    list: {
      title: "Referrals",
      totalCount: "{{count}} total",
      subtitle: "All referrals – drafts, signed, sent and answered",
      recipients: "Recipients",
      create: "Create referral",
      all: "All",
      moreFilters: "More filters",
      allSenders: "All senders",
      allCreators: "All",
      fromDate: "From",
      toDate: "To",
      clearFilters: "Clear filters",
      studentAttributes: "Attributes",
      searchPlaceholder: "Search student, recipient...",
      stats: {
        total: "Total",
        draft: "Drafts",
        active: "Active",
        awaiting: "Awaiting response",
      },
      responseFilter: {
        all: "All responses",
        received: "Response received",
        missing: "Response missing",
      },
      columns: {
        student: "Student",
        recipient: "Recipient",
        requestedExamination: "Requested examination",
        sender: "Sender",
        status: "Status",
        createdAt: "Created",
        createdBy: "Created by",
      },
      emptyTitle: "No referrals yet",
      emptySubtitle:
        "Create a referral to refer a student to e.g. BUP, a health center or a specialist clinic.",
      noMatchTitle: "No matching referrals",
      noMatchSubtitle: "Try changing the filters or search.",
    },
    wizard: {
      title: "Create referral",
      editTitle: "Edit referral",
      stepOf: "Step {{step}} of {{total}}",
      steps: {
        student: "Student",
        recipient: "Recipient",
        content: "Content",
        sender: "Sender",
      },
      studentHint:
        "Select the student the referral concerns. A referral is for one student.",
      studentLocked: "The student cannot be changed on an existing referral.",
      studentFromProcess:
        "The referral is created for the student in this process.",
      searchStudent: "Search student...",
      noStudents: "No students found.",
      savedRecipient: "Saved recipient",
      manualRecipient: "Enter manually",
      noSavedRecipients: "There are no saved recipients yet.",
      saveAsRecipient: "Save as recipient for future referrals",
      consentLabel: "Consent from guardian/student is confirmed",
      senderHint:
        "Select who is shown as sender on the referral. The logged-in user signs.",
      staffFallbackRole: "Student health staff",
      discardTitle: "Discard changes?",
      discardMessage:
        "You have unsaved changes. Are you sure you want to leave without saving?",
      continueEditing: "Continue Editing",
      discardChanges: "Discard Changes",
    },
    validation: {
      tooLong: "The text is too long",
      nameTooLong: "The name is too long (max 100 characters)",
    },
    fields: {
      recipientName: "Name",
      recipientType: "Recipient type",
      organization: "Organization",
      department: "Department",
      phone: "Phone",
      email: "Email",
      address: "Address",
      postalCode: "Postal code",
      city: "City",
      country: "Country",
      notes: "Notes",
      requestedExamination: "Requested examination",
      referralReason: "Referral question",
      referralText: "Referral text",
      background: "Background",
      previousActions: "Previous interventions",
      consent: "Consent",
      internalNote: "Internal note",
      attachment: "Attachment",
      sentAt: "Sent date",
      sentMethod: "Method",
      note: "Note",
      uploadResponse: "Upload response",
      responseReceivedAt: "Response received date",
      responseSummary: "Response summary",
      closeNote: "Closing note",
      reason: "Reason",
    },
    placeholders: {
      recipientName: "Recipient name",
      organization: "E.g. Region Skåne",
      department: "E.g. BUP",
      phone: "Phone",
      email: "name@example.com",
      address: "Street and number",
      postalCode: "XXX XX",
      city: "City",
      requestedExamination: "E.g. ADHD assessment, speech assessment",
      referralReason: "What should the recipient assess?",
      referralText: "Main referral content",
      background: "Relevant context",
      previousActions: "What the school/EMI has already done",
      consentNote: "E.g. guardian consents via signed consent",
      internalNote: "Internal only, not printed",
      responseSummary:
        "E.g. BUP received the referral and will call the guardian.",
    },
    actions: {
      saveDraft: "Save draft",
      saveAndSign: "Save and sign",
      sign: "Sign",
      edit: "Edit",
      cancel: "Cancel referral",
      delete: "Delete draft",
      unlock: "Unlock",
      downloadPdf: "Download PDF",
      markSent: "Mark as sent",
      uploadResponse: "Upload referral response",
      registerResponse: "Register response without file",
      close: "Close referral",
    },
    confirm: {
      signTitle: "Sign referral",
      signMessage:
        "You are about to sign this referral. After signing, it will be locked for editing. Do you want to continue?",
      signNameLabel: "Type your name to confirm",
      signNameMismatch: "The name doesn't match your account name.",
      unlockTitle: "Unlock referral",
      unlockMessage:
        "The referral returns to draft so it can be edited. The unlock is logged with a reason.",
      cancelTitle: "Cancel referral",
      cancelMessage:
        "The referral is cancelled and cannot be sent. This action is logged.",
      deleteTitle: "Delete draft",
      deleteMessage:
        "The draft is deleted. Its history is kept in the archive.",
      closeTitle: "Close referral",
      closeMessage: "The referral process is marked as completed.",
      closeWithoutResponseMessage:
        "No referral response is registered. Explain why the referral is closed without a response.",
    },
    markSent: {
      title: "Mark as sent",
      subtitle: "Enter when and how the referral was sent.",
    },
    response: {
      uploadTitle: "Upload referral response",
      uploadSubtitle: "Upload the received response and enter when it arrived.",
      registerTitle: "Register response",
      registerSubtitle:
        "Register that a response was received without uploading a file.",
      submit: "Register response",
      fileTooLarge: "The file is too large (max 20 MB).",
    },
    recipients: {
      title: "Saved recipients",
      subtitle: "Manage recurring referral recipients",
      add: "Add recipient",
      addTitle: "Add recipient",
      editTitle: "Edit recipient",
      selectType: "Select type",
      empty: "No saved recipients yet.",
      removeTitle: "Remove recipient",
      removeMessage:
        "{{name}} will be removed from saved recipients. Existing referrals are not affected.",
    },
    detail: {
      title: "Referral • {{examination}}",
      to: "To {{recipient}}",
      notFound: "The referral was not found.",
      draftBanner:
        "This is a draft and can be edited freely until it is signed.",
      recipient: "Recipient",
      sender: "Sender",
      createdBy: "Created by",
      created: "Created",
      signed: "Signed",
      sent: "Sent",
      responseReceived: "Response received",
      closed: "Closed",
      actions: "Actions",
      history: "History",
      noHistory: "No history yet.",
      system: "System",
      consentConfirmed: "Consent confirmed",
      consentNotConfirmed: "Not confirmed",
      response: "Referral response",
      responseReceivedOn: "Received {{date}}",
      documents: "Documents",
      documentAttachment: "Attachment",
      documentResponse: "Referral response",
    },
    events: {
      created: "Created",
      updated_draft: "Draft updated",
      signed: "Signed",
      unlocked: "Unlocked",
      marked_sent: "Marked as sent",
      response_uploaded: "Response received and uploaded",
      response_registered: "Response received and registered",
      closed: "Closed",
      cancelled: "Cancelled",
      deleted: "Deleted",
      exported: "PDF downloaded",
    },
    notify: {
      draftSaved: "Draft saved",
      signed: "Referral signed",
      attachmentFailed:
        "The referral was saved but the attachment could not be uploaded. Edit the draft to try again.",
      unlocked: "Referral unlocked",
      cancelled: "Referral cancelled",
      deleted: "Draft deleted",
      markedSent: "Referral marked as sent",
      responseRegistered: "Referral response registered",
      closed: "Referral closed",
      recipientSaved: "Recipient saved",
      recipientRemoved: "Recipient removed",
    },
    errors: {
      generic: "Something went wrong. Please try again.",
      REFERRAL_PERMISSION_DENIED: "You do not have permission for referrals.",
      REFERRAL_NOT_FOUND: "The referral was not found.",
      REFERRAL_RECIPIENT_NOT_FOUND: "The recipient was not found.",
      REFERRAL_STUDENT_NOT_FOUND: "The student was not found.",
      REFERRAL_SENDER_NOT_FOUND: "The sender was not found.",
      REFERRAL_INVALID_STATUS:
        "This action is not allowed in the referral's current status.",
      REFERRAL_NOT_EDITABLE: "Only drafts can be edited.",
      REFERRAL_MISSING_REQUIRED_FIELDS: "Required fields are missing.",
      REFERRAL_REASON_REQUIRED: "Please enter a reason.",
      REFERRAL_RESPONSE_SUMMARY_REQUIRED: "Please enter a response summary.",
      REFERRAL_CLOSE_NOTE_REQUIRED:
        "Explain why the referral is closed without a response.",
    },
    pdf: {
      heading: "Referral",
      date: "Date",
      referralId: "Referral ID",
      student: "Student",
      recipient: "Recipient",
      sender: "Sender",
      name: "Name",
      personalIdentity: "Personal identity number",
      birthday: "Date of birth",
      class: "Class",
      school: "School",
      organization: "Organization",
      department: "Department",
      address: "Address",
      phone: "Phone",
      email: "Email",
      role: "Title/role",
      signed: "Signed",
      attachments: "Attachments",
      content: "Referral content",
      summary: "Summary",
      generatedBy: "Generated by:",
    },
  },
};

export default en;
